import React, { useState, useEffect, useMemo } from "react";
import MakoConnectCard from "../MakoConnectCard";
import MakoTextCard from "../MakoTextCard";
import OrcaConnectCard from "../OrcaConnectCard";
import GithubBackupCard from "../GithubBackupCard";
import { HelpDot } from "../AgenticGlossary";
import { useUser } from "@clerk/clerk-react";
import IntegrationCard from "../integration-components/IntegrationCard";
import IntegrationModal from "../integration-components/IntegrationModal";
import { apiUrl, authFetch } from "../../../utils/api";
import { useFeatureFlag } from "../../../config/featureFlags";
import { removeOAuthCallbackParams } from "../../../adapters/oauthCallbackUrl";

/**
 * IntegrationsTab - Manages all API integrations including BYOK LLM keys
 *
 * Features:
 * - Dynamic integration loading from backend registry
 * - Claude API key management (BYOK)
 * - Test/save/delete credentials via Fort Knox
 * - Real-time status updates
 * - Error handling
 */
const IntegrationsTab = () => {
  const { user } = useUser();

  // BYOK card visibility.
  // Flat $297 pricing (ARCHITECTURE_LOCKED.md): everyone brings their own
  // Anthropic OAuth, so the BYOK card shows for ALL users. The backend's
  // system_key_fallback flag can re-open the platform-key fallback for
  // subscribers/prospects, but even then their own key takes over when
  // pasted — the card stays useful either way.
  // Mirrors chatApiRoutes.allowsSystemKeyFallback() + c3 credentialResolver.
  const canUseBYOK = true;

  // Canva OAuth feature flag (disabled until backend is deployed)
  const canvaEnabled = useFeatureFlag("canva_oauth");

  // Custom Tools feature flag
  const customToolsEnabled = useFeatureFlag("custom_tools");
  const customOAuthRefreshEnabled = useFeatureFlag("custom_mcp_oauth_refresh");

  // Claude Max feature flag (OFF: Anthropic restricted 3rd-party setup tokens)
  const claudeMaxEnabled = useFeatureFlag("claude_max");

  // Overflow Claude token failover (telegram-bridge OVERFLOW pattern)
  const claudeOverflowEnabled = useFeatureFlag("claude_overflow_failover");

  // Feature flags from backend
  const [featureFlags, setFeatureFlags] = useState({});

  // All possible static integrations (filtered by feature flags)
  const ALL_STATIC_INTEGRATIONS = [
    {
      id: "PERPLEXITY",
      name: "Perplexity (Internet Research)",
      description:
        "Search the web and get AI-synthesized answers with citations. Powers the internet_research tool.",
      category: "research",
      authType: "api_key",
      icon: "🔍",
      featureFlag: "internet_research", // Links to backend feature flag
      fields: [
        {
          name: "api_key",
          label: "Perplexity API Key",
          type: "password",
          placeholder: "pplx-...",
          required: true,
        },
      ],
      messages: {
        connecting: "Testing Perplexity API key...",
        testSuccess: "Perplexity API key is valid!",
        testFailure: "Invalid API key - please check and try again",
        saveSuccess:
          "Perplexity API key saved! Internet research is now available.",
        saveFailure: "Failed to save API key",
      },
      helpText:
        "Get your API key at perplexity.ai/settings/api. ~$0.006 per search.",
      enabled: true,
      comingSoon: false,
    },
  ];

  // Filter static integrations based on feature flags
  const STATIC_INTEGRATIONS = useMemo(() => {
    return ALL_STATIC_INTEGRATIONS.filter((integration) => {
      // If no feature flag defined, always show
      if (!integration.featureFlag) return true;
      // Otherwise, check if the flag is enabled
      return featureFlags[integration.featureFlag] === true;
    });
  }, [featureFlags]);

  // Claude Max state (setup token connection)
  const [claudeMaxStatus, setClaudeMaxStatus] = useState({
    connected: false,
    loading: true,
    tokenType: null,
    tokenHealth: null,
    expiresAt: null,
    error: null,
  });

  // Check Claude Max status on mount
  useEffect(() => {
    checkClaudeMaxStatus();
  }, []);

  const checkClaudeMaxStatus = async () => {
    try {
      setClaudeMaxStatus(prev => ({ ...prev, loading: true, error: null }));
      const response = await authFetch(apiUrl('/api/claude-max/status'));
      const data = await response.json();
      setClaudeMaxStatus({
        connected: data.connected || false,
        loading: false,
        tokenType: data.tokenType,
        tokenHealth: data.tokenHealth,
        expiresAt: data.expiresAt,
        hasRefreshToken: data.hasRefreshToken,
        minutesUntilExpiry: data.minutesUntilExpiry,
        error: data.error || null,
        overflow: data.overflow || { connected: false },
      });
    } catch (error) {
      console.error('Failed to check Claude Max status:', error);
      setClaudeMaxStatus(prev => ({
        ...prev,
        connected: false,
        loading: false,
        error: 'Failed to check status',
      }));
    }
  };

  const handleClaudeMaxConnect = async (token) => {
    try {
      setClaudeMaxStatus(prev => ({ ...prev, loading: true, error: null }));
      const response = await authFetch(apiUrl('/api/claude-max/connect'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const data = await response.json();

      if (data.success) {
        await checkClaudeMaxStatus();
        alert(`✅ Claude Max connected! Using ${data.tokenType === 'setup_token' ? 'subscription quota' : 'API billing'}`);
        return true;
      } else {
        const errorMsg = data.details || data.error || 'Failed to connect';
        setClaudeMaxStatus(prev => ({ ...prev, loading: false, error: errorMsg }));
        alert(`❌ ${errorMsg}`);
        return false;
      }
    } catch (error) {
      console.error('Claude Max connect error:', error);
      setClaudeMaxStatus(prev => ({ ...prev, loading: false, error: error.message }));
      alert(`❌ ${error.message}`);
      return false;
    }
  };

  const handleClaudeMaxDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect Claude Max?')) return;

    try {
      setClaudeMaxStatus(prev => ({ ...prev, loading: true }));
      const response = await authFetch(apiUrl('/api/claude-max/disconnect'), {
        method: 'DELETE',
      });
      const data = await response.json();

      if (data.success) {
        setClaudeMaxStatus({
          connected: false,
          loading: false,
          tokenType: null,
          tokenHealth: null,
          expiresAt: null,
          error: null,
        });
        alert('✅ Claude Max disconnected');
      } else {
        setClaudeMaxStatus(prev => ({ ...prev, loading: false, error: data.error }));
      }
    } catch (error) {
      console.error('Claude Max disconnect error:', error);
      setClaudeMaxStatus(prev => ({ ...prev, loading: false, error: error.message }));
    }
  };

  const handleClaudeMaxRefresh = async () => {
    try {
      setClaudeMaxStatus(prev => ({ ...prev, loading: true }));
      const response = await authFetch(apiUrl('/api/claude-max/refresh'), {
        method: 'POST',
      });
      const data = await response.json();

      if (data.success) {
        await checkClaudeMaxStatus();
        alert('✅ Token refreshed successfully');
      } else {
        setClaudeMaxStatus(prev => ({ ...prev, loading: false, error: data.error }));
        alert(`❌ ${data.error || 'Refresh failed'}`);
      }
    } catch (error) {
      console.error('Claude Max refresh error:', error);
      setClaudeMaxStatus(prev => ({ ...prev, loading: false, error: error.message }));
    }
  };

  const handleClaudeMaxTest = async () => {
    try {
      setClaudeMaxStatus(prev => ({ ...prev, loading: true }));
      const response = await authFetch(apiUrl('/api/claude-max/test'), {
        method: 'POST',
      });
      const data = await response.json();

      setClaudeMaxStatus(prev => ({ ...prev, loading: false }));

      if (data.success) {
        alert(`✅ Token valid! Model: ${data.model}`);
      } else {
        alert(`❌ ${data.error || 'Test failed'}`);
      }
    } catch (error) {
      console.error('Claude Max test error:', error);
      setClaudeMaxStatus(prev => ({ ...prev, loading: false }));
      alert(`❌ ${error.message}`);
    }
  };

  // ── Overflow Claude token (claude_overflow_failover) ──────────────
  // Optional second token that automatically catches plan-limit 429s on
  // the primary connection. Saved via the same Fort Knox path.
  const handleOverflowConnect = async (token) => {
    try {
      setClaudeMaxStatus(prev => ({ ...prev, loading: true }));
      const response = await authFetch(apiUrl('/api/claude-max/overflow/connect'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const data = await response.json();
      if (data.success) {
        await checkClaudeMaxStatus();
        alert(`✅ ${data.message || 'Overflow Claude token connected'}`);
        return true;
      }
      setClaudeMaxStatus(prev => ({ ...prev, loading: false }));
      alert(`❌ ${data.details || data.error || 'Failed to save overflow token'}`);
      return false;
    } catch (error) {
      console.error('Overflow connect error:', error);
      setClaudeMaxStatus(prev => ({ ...prev, loading: false }));
      alert(`❌ ${error.message}`);
      return false;
    }
  };

  const handleOverflowRemove = async () => {
    if (!confirm('Remove your overflow Claude token?')) return;
    try {
      setClaudeMaxStatus(prev => ({ ...prev, loading: true }));
      const response = await authFetch(apiUrl('/api/claude-max/overflow'), { method: 'DELETE' });
      const data = await response.json();
      if (data.success) {
        await checkClaudeMaxStatus();
        alert('✅ Overflow Claude token removed');
      } else {
        setClaudeMaxStatus(prev => ({ ...prev, loading: false }));
        alert(`❌ ${data.error || 'Failed to remove overflow token'}`);
      }
    } catch (error) {
      console.error('Overflow remove error:', error);
      setClaudeMaxStatus(prev => ({ ...prev, loading: false }));
      alert(`❌ ${error.message}`);
    }
  };

  // LLM Provider configs (for AI Provider Keys section)
  const LLM_INTEGRATIONS = [
    // Claude Max (setup token) — only shown when claude_max flag is ON
    ...(claudeMaxEnabled ? [{
      id: "CLAUDE_MAX",
      name: "Claude Max (Subscription)",
      description: "Use your Claude Pro/Max subscription instead of API billing. One-time setup, lasts ~1 year.",
      category: "llm",
      authType: "setup_token",
      icon: "✨",
      isClaudeMax: true, // Special flag for custom rendering
      fields: [
        {
          name: "token",
          label: "Setup Token",
          type: "password",
          placeholder: "sk-ant-oat01-...",
          required: true,
        },
      ],
      messages: {
        connecting: "Validating setup token...",
        testSuccess: "Setup token is valid!",
        testFailure: "Invalid token - please check and try again",
        saveSuccess: "Claude Max connected successfully!",
        saveFailure: "Failed to connect",
      },
      helpText: "One-time setup via terminal. Token lasts ~1 year (or until you rotate it).",
      // Canonical install flow — mirrors One Hour to Agents Rung 3 (portal
      // lesson, Brad's 2026-10-19 Windows revision: edit the EXISTING Path row,
      // PowerShell as Administrator, Ctrl+Shift+V via Properties, the >>
      // continuation fix, claude --version check). ONE command, no
      // Node/Homebrew/Xcode. If the lesson changes, change this too.
      setupInstructions: [
        "1. Open your terminal. Mac: Cmd+Space, type \"Terminal\". Windows: press the Windows key, type \"PowerShell\", right-click Windows PowerShell → Run as administrator → Yes. The title bar must say \"Administrator: Windows PowerShell\" — if it doesn't, close it and open it again the right way.",
        "2. Windows only, before installing: (a) Path — Windows key, type \"environment variables\" → Edit the system environment variables → Environment Variables… → in the TOP box click the row named Path → Edit… → New → type exactly  %USERPROFILE%\\.local\\bin  → OK on every window. You are editing the EXISTING Path row, never creating a new variable (a second variable means the install is never found). (b) Paste — right-click the PowerShell title bar → Properties → Options → tick \"Use Ctrl+Shift+C/V as Copy/Paste\" → OK. From now on Ctrl+Shift+V pastes.",
        "3. Install Claude Code — one command, no Node/Homebrew needed:",
        "   Mac:  curl -fsSL https://claude.ai/install.sh | bash",
        "   Windows (Administrator PowerShell):  irm https://claude.ai/install.ps1 | iex",
        "   Windows: if the next line shows >> instead of the install starting, press Backspace once on that line, then Enter.",
        "4. Check it took: run  claude --version  — you should see a version number. Mac \"command not found\": run  echo 'export PATH=\"$HOME/.local/bin:$PATH\"' >> ~/.zshrc  then close and reopen Terminal. Windows \"not recognized\": close PowerShell, reopen it as Administrator (the Path change only applies to windows opened after it).",
        "5. Run: claude setup-token — sign in when the browser opens; it prints a token starting sk-ant-oat01-…",
        "6. Paste that token above. Lasts ~1 year. Requires Claude Pro/Max (~$20/mo).",
        "Important: it must start with sk-ant-oat01- (OAuth token), NOT sk-ant-api03- (API key) — an API key here won't work and would bill full API rates per message.",
      ],
      enabled: true,
      comingSoon: false,
    }] : []),
    // OAuth-only for Claude (Brad directive): the raw sk-ant-api key card is
    // removed. Users connect Claude via `claude setup-token` → the Claude Max
    // (setup token) card above. No raw API keys for Claude.
  ];

  const [integrations, setIntegrations] = useState([]);
  const [llmIntegrations, setLlmIntegrations] = useState(LLM_INTEGRATIONS);
  const [dynamicMCPs, setDynamicMCPs] = useState([]);
  const [integrationSearch, setIntegrationSearch] = useState(""); // Search filter for integrations
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [selectedCredentials, setSelectedCredentials] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingCredentials, setIsLoadingCredentials] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResults, setTestResults] = useState(null);

  // Google OAuth state
  const [googleOAuth, setGoogleOAuth] = useState({
    connected: false,
    loading: true,
    scopes: [],
    expiresAt: null,
    connecting: false,
    disconnecting: false,
    refreshing: false,
    error: null,
    tokenHealth: null,
    minutesUntilExpiry: null,
  });

  // Google Services state (Drive, Calendar, IAM, etc.)
  const [googleServices, setGoogleServices] = useState({
    loading: false,
    services: [],
    error: null,
    togglingService: null, // Track which service is being toggled
  });

  // Bulk auth mode - collect services then auth all at once
  const [bulkAuthMode, setBulkAuthMode] = useState(false);
  const [pendingServices, setPendingServices] = useState([]); // Services selected for bulk auth
  const [bulkAuthLoading, setBulkAuthLoading] = useState(false);

  // Canva OAuth state
  const [canvaOAuth, setCanvaOAuth] = useState({
    connected: false,
    loading: true,
    scopes: [],
    expiresAt: null,
    connecting: false,
    disconnecting: false,
    refreshing: false,
    error: null,
    tokenHealth: null,
    minutesUntilExpiry: null,
  });

  // Canva Services state
  const [canvaServices, setCanvaServices] = useState({
    loading: false,
    services: [],
    error: null,
    togglingService: null,
  });

  // Custom Tools state
  const [customTools, setCustomTools] = useState([]);
  const [customToolsLoading, setCustomToolsLoading] = useState(false);
  const [customToolForm, setCustomToolForm] = useState({
    platform_name: "",
    credentials: [{ name: "API_KEY", value: "" }], // dynamic credential rows
  });
  const [customToolSubmitting, setCustomToolSubmitting] = useState(false);
  const [customToolError, setCustomToolError] = useState(null);
  const [customToolSuccess, setCustomToolSuccess] = useState(null);
  const [customToolCredEdit, setCustomToolCredEdit] = useState({}); // {toolId: true/false}
  const [customToolCredValues, setCustomToolCredValues] = useState({}); // {toolId: [{name, value}]}
  const [customToolCredSaving, setCustomToolCredSaving] = useState({}); // {toolId: true/false}

  // Load feature flags first, then integrations
  useEffect(() => {
    loadFeatureFlags();
    checkGoogleOAuthStatus();
    checkCanvaOAuthStatus();
    handleOAuthCallback();
    loadCustomTools();
  }, []);

  // Auto-refresh tokens before expiry (check every 5 minutes)
  useEffect(() => {
    const checkAndRefreshTokens = async () => {
      // Auto-refresh Google if needed
      if (googleOAuth.connected &&
          (googleOAuth.tokenHealth === "needs_refresh" || googleOAuth.tokenHealth === "expiring_soon") &&
          !googleOAuth.refreshing) {
        console.log("[IntegrationsTab] Auto-refreshing Google token");
        handleRefreshToken();
      }

      // Auto-refresh Canva if needed
      if (canvaOAuth.connected &&
          (canvaOAuth.tokenHealth === "needs_refresh" || canvaOAuth.tokenHealth === "expiring_soon") &&
          !canvaOAuth.refreshing) {
        console.log("[IntegrationsTab] Auto-refreshing Canva token");
        handleCanvaRefreshToken();
      }
    };

    // Check immediately on mount and when token health changes
    checkAndRefreshTokens();

    // Set up interval to check every 5 minutes
    const interval = setInterval(checkAndRefreshTokens, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [googleOAuth.connected, googleOAuth.tokenHealth, canvaOAuth.connected, canvaOAuth.tokenHealth]);

  // Load integrations when feature flags are loaded
  useEffect(() => {
    loadPublishedMCPs();
    loadIntegrations();
  }, [featureFlags]);

  // Check for OAuth callback in URL
  const handleOAuthCallback = () => {
    const params = new URLSearchParams(window.location.search);
    const oauthStatus = params.get("oauth");
    const provider = params.get("provider");
    const error = params.get("error");

    // If we're in a popup (opened by window.open), close it after OAuth completes
    const isPopup = window.opener && window.opener !== window;

    if (oauthStatus && provider === "google") {
      // Clean up URL
      window.history.replaceState({}, "", removeOAuthCallbackParams(window.location));

      if (oauthStatus === "success") {
        // Refresh Google OAuth status
        checkGoogleOAuthStatus();
      } else if (oauthStatus === "error") {
        setGoogleOAuth((prev) => ({
          ...prev,
          error: error || "OAuth connection failed",
          loading: false,
        }));
      }
    }

    if (oauthStatus && provider === "canva") {
      // Clean up URL
      window.history.replaceState({}, "", removeOAuthCallbackParams(window.location));

      if (oauthStatus === "success") {
        // If in popup, close it - the parent window will poll and refresh
        if (isPopup) {
          window.close();
          return;
        }
        // Refresh Canva OAuth status
        checkCanvaOAuthStatus();
      } else if (oauthStatus === "error") {
        setCanvaOAuth((prev) => ({
          ...prev,
          error: error || "OAuth connection failed",
          loading: false,
        }));
        // Close popup even on error so user sees error in main window
        if (isPopup) {
          window.close();
          return;
        }
      }
    }
  };

  // Check Google OAuth connection status
  const checkGoogleOAuthStatus = async () => {
    try {
      setGoogleOAuth((prev) => ({ ...prev, loading: true, error: null }));

      const response = await authFetch(
        apiUrl(`/api/oauth/google/status?user_id=${user?.id}`),
      );
      const data = await response.json();

      const isConnected = data.connected || false;

      setGoogleOAuth((prev) => ({
        ...prev,
        connected: isConnected,
        scopes: data.scopes || [],
        expiresAt: data.expires_at || null,
        tokenHealth: data.token_health || null,
        minutesUntilExpiry: data.minutes_until_expiry || null,
        loading: false,
      }));

      // If connected, load services
      if (isConnected) {
        loadGoogleServices();
      }
    } catch (error) {
      console.error("Failed to check Google OAuth status:", error);
      setGoogleOAuth((prev) => ({
        ...prev,
        connected: false,
        loading: false,
        error: "Failed to check connection status",
      }));
    }
  };

  // Manually refresh OAuth token
  const handleRefreshToken = async () => {
    try {
      setGoogleOAuth((prev) => ({ ...prev, refreshing: true, error: null }));

      const response = await authFetch(
        apiUrl(`/api/oauth/google/refresh?user_id=${user?.id}`),
        { method: "POST" },
      );
      const data = await response.json();

      if (data.success) {
        // Refresh status to get updated token info
        await checkGoogleOAuthStatus();
        // Reset refreshing state after successful refresh
        setGoogleOAuth((prev) => ({ ...prev, refreshing: false }));
      } else {
        setGoogleOAuth((prev) => ({
          ...prev,
          refreshing: false,
          error: data.error || "Failed to refresh token",
        }));
        setTimeout(() => {
          setGoogleOAuth((prev) => ({ ...prev, error: null }));
        }, 5000);
      }
    } catch (error) {
      console.error("Failed to refresh token:", error);
      setGoogleOAuth((prev) => ({
        ...prev,
        refreshing: false,
        error: "Failed to refresh token",
      }));
      setTimeout(() => {
        setGoogleOAuth((prev) => ({ ...prev, error: null }));
      }, 5000);
    }
  };

  // Load Google Services list
  const loadGoogleServices = async () => {
    try {
      setGoogleServices((prev) => ({ ...prev, loading: true, error: null }));

      const response = await authFetch(
        apiUrl(`/api/oauth/google/services?user_id=${user?.id}`),
      );
      const data = await response.json();

      if (data.success) {
        setGoogleServices((prev) => ({
          ...prev,
          services: data.services || [],
          loading: false,
        }));
      } else {
        throw new Error(data.error || "Failed to load services");
      }
    } catch (error) {
      console.error("Failed to load Google services:", error);
      setGoogleServices((prev) => ({
        ...prev,
        loading: false,
        error: "Failed to load services",
      }));
    }
  };

  // Toggle a Google service on/off
  // State for incremental auth modal
  const [incrementalAuthModal, setIncrementalAuthModal] = useState({
    show: false,
    serviceId: null,
    serviceName: null,
    missingScopes: [],
    loading: false,
  });

  // Toggle a service in bulk auth mode (just adds/removes from pending list)
  const handleTogglePendingService = (serviceId) => {
    setPendingServices((prev) =>
      prev.includes(serviceId)
        ? prev.filter((id) => id !== serviceId)
        : [...prev, serviceId],
    );
  };

  // Bulk authorize all pending services at once
  const handleBulkAuth = async () => {
    if (pendingServices.length === 0) return;

    try {
      setBulkAuthLoading(true);

      // Request incremental auth for ALL selected services at once
      const serviceIds = pendingServices.join(",");
      const response = await authFetch(
        apiUrl(
          `/api/oauth/google/authorize-incremental?user_id=${user?.id}&service_ids=${serviceIds}`,
        ),
      );
      const data = await response.json();

      if (data.authorization_url) {
        // Redirect to Google OAuth for ALL permissions at once
        window.location.href = data.authorization_url;
      } else {
        throw new Error(data.error || "Failed to start authorization");
      }
    } catch (error) {
      console.error("Bulk auth failed:", error);
      setBulkAuthLoading(false);
      setGoogleServices((prev) => ({
        ...prev,
        error: error.message || "Failed to request permissions",
      }));
      setTimeout(() => {
        setGoogleServices((prev) => ({ ...prev, error: null }));
      }, 3000);
    }
  };

  // Exit bulk auth mode and clear pending
  const handleCancelBulkAuth = () => {
    setBulkAuthMode(false);
    setPendingServices([]);
  };

  const handleToggleGoogleService = async (serviceId, currentlyEnabled) => {
    // If in bulk auth mode, just toggle the pending list
    if (bulkAuthMode && !currentlyEnabled) {
      handleTogglePendingService(serviceId);
      return;
    }

    try {
      setGoogleServices((prev) => ({ ...prev, togglingService: serviceId }));

      // If disabling, just disable
      if (currentlyEnabled) {
        const response = await authFetch(
          apiUrl(
            `/api/oauth/google/services/${serviceId}/disable?user_id=${user?.id}`,
          ),
          { method: "DELETE" },
        );
        const data = await response.json();

        if (data.success) {
          setGoogleServices((prev) => ({
            ...prev,
            services: prev.services.map((s) =>
              s.id === serviceId ? { ...s, enabled: false } : s,
            ),
            togglingService: null,
          }));
          // Also remove from pending if in bulk mode
          setPendingServices((prev) => prev.filter((id) => id !== serviceId));
        } else {
          throw new Error(data.error || "Failed to disable service");
        }
        return;
      }

      // If enabling, check for required scopes first
      const response = await authFetch(
        apiUrl(
          `/api/oauth/google/services/${serviceId}/enable?user_id=${user?.id}`,
        ),
        { method: "POST" },
      );
      const data = await response.json();

      if (data.success) {
        // Scopes already present - service enabled
        setGoogleServices((prev) => ({
          ...prev,
          services: prev.services.map((s) =>
            s.id === serviceId ? { ...s, enabled: true } : s,
          ),
          togglingService: null,
        }));
      } else if (data.needs_reauth) {
        // Missing scopes - in normal mode show modal, but suggest bulk mode
        const serviceName = serviceId.replace("GOOGLE_", "").replace(/_/g, " ");
        setIncrementalAuthModal({
          show: true,
          serviceId: serviceId,
          serviceName: serviceName,
          missingScopes: data.missing_scopes || [],
          loading: false,
        });
        setGoogleServices((prev) => ({ ...prev, togglingService: null }));
      } else if (data.needs_oauth_connect) {
        // No OAuth connection - prompt to connect first
        setGoogleServices((prev) => ({
          ...prev,
          togglingService: null,
          error: "Please connect Google OAuth first",
        }));
        setTimeout(() => {
          setGoogleServices((prev) => ({ ...prev, error: null }));
        }, 3000);
      } else {
        throw new Error(data.error || "Failed to enable service");
      }
    } catch (error) {
      console.error(`Failed to toggle Google service ${serviceId}:`, error);
      setGoogleServices((prev) => ({
        ...prev,
        togglingService: null,
        error: error.message || "Failed to toggle service",
      }));
      // Clear error after 3 seconds
      setTimeout(() => {
        setGoogleServices((prev) => ({ ...prev, error: null }));
      }, 3000);
    }
  };

  // Handle incremental authorization (when user confirms re-auth)
  const handleIncrementalAuth = async () => {
    try {
      setIncrementalAuthModal((prev) => ({ ...prev, loading: true }));

      const { serviceId } = incrementalAuthModal;

      // Request incremental auth for this service
      const response = await authFetch(
        apiUrl(
          `/api/oauth/google/authorize-incremental?user_id=${user?.id}&service_ids=${serviceId}`,
        ),
      );
      const data = await response.json();

      if (data.authorization_url) {
        // Redirect to Google OAuth for additional permissions
        window.location.href = data.authorization_url;
      } else {
        throw new Error(data.error || "Failed to start authorization");
      }
    } catch (error) {
      console.error("Incremental auth failed:", error);
      setIncrementalAuthModal((prev) => ({
        ...prev,
        loading: false,
      }));
      setGoogleServices((prev) => ({
        ...prev,
        error: error.message || "Failed to request permissions",
      }));
      setTimeout(() => {
        setGoogleServices((prev) => ({ ...prev, error: null }));
      }, 3000);
    }
  };

  // Close incremental auth modal
  const handleCloseIncrementalAuthModal = () => {
    setIncrementalAuthModal({
      show: false,
      serviceId: null,
      serviceName: null,
      missingScopes: [],
      loading: false,
    });
  };

  // Connect Google OAuth
  const handleGoogleConnect = async () => {
    try {
      setGoogleOAuth((prev) => ({ ...prev, connecting: true, error: null }));

      const response = await authFetch(
        apiUrl(`/api/oauth/google/authorize?user_id=${user?.id}`),
      );
      const data = await response.json();

      if (data.authorization_url) {
        // Redirect to Google OAuth
        window.location.href = data.authorization_url;
      } else {
        throw new Error("No authorization URL returned");
      }
    } catch (error) {
      console.error("Failed to initiate Google OAuth:", error);
      setGoogleOAuth((prev) => ({
        ...prev,
        connecting: false,
        error: "Failed to start Google connection",
      }));
    }
  };

  // Disconnect Google OAuth
  const handleGoogleDisconnect = async () => {
    if (!confirm("Are you sure you want to disconnect your Google account?")) {
      return;
    }

    try {
      setGoogleOAuth((prev) => ({ ...prev, disconnecting: true, error: null }));

      const response = await authFetch(
        apiUrl(`/api/oauth/google/disconnect?user_id=${user?.id}`),
        { method: "DELETE" },
      );
      const data = await response.json();

      if (data.success) {
        setGoogleOAuth((prev) => ({
          ...prev,
          connected: false,
          scopes: [],
          expiresAt: null,
          disconnecting: false,
        }));
      } else {
        throw new Error(data.message || "Disconnect failed");
      }
    } catch (error) {
      console.error("Failed to disconnect Google OAuth:", error);
      setGoogleOAuth((prev) => ({
        ...prev,
        disconnecting: false,
        error: "Failed to disconnect Google account",
      }));
    }
  };

  // ==========================================================================
  // CANVA OAUTH HANDLERS
  // ==========================================================================

  // Check Canva OAuth connection status
  const checkCanvaOAuthStatus = async () => {
    try {
      setCanvaOAuth((prev) => ({ ...prev, loading: true, error: null }));

      const response = await authFetch(
        apiUrl(`/api/oauth/canva/status?user_id=${user?.id}`),
      );
      const data = await response.json();

      const isConnected = data.connected || false;

      setCanvaOAuth((prev) => ({
        ...prev,
        connected: isConnected,
        scopes: data.scopes || [],
        expiresAt: data.expires_at || null,
        tokenHealth: data.token_health || null,
        minutesUntilExpiry: data.minutes_until_expiry || null,
        loading: false,
      }));

      // If connected, load services
      if (isConnected) {
        loadCanvaServices();
      }
    } catch (error) {
      console.error("Failed to check Canva OAuth status:", error);
      setCanvaOAuth((prev) => ({
        ...prev,
        connected: false,
        loading: false,
        error: "Failed to check connection status",
      }));
    }
  };

  // Manually refresh Canva OAuth token
  const handleCanvaRefreshToken = async () => {
    try {
      setCanvaOAuth((prev) => ({ ...prev, refreshing: true, error: null }));

      const response = await authFetch(
        apiUrl(`/api/oauth/canva/refresh?user_id=${user?.id}`),
        { method: "POST" },
      );
      const data = await response.json();

      if (data.success) {
        await checkCanvaOAuthStatus();
        setCanvaOAuth((prev) => ({ ...prev, refreshing: false }));
      } else {
        setCanvaOAuth((prev) => ({
          ...prev,
          refreshing: false,
          error: data.error || "Failed to refresh token",
        }));
        setTimeout(() => {
          setCanvaOAuth((prev) => ({ ...prev, error: null }));
        }, 5000);
      }
    } catch (error) {
      console.error("Failed to refresh Canva token:", error);
      setCanvaOAuth((prev) => ({
        ...prev,
        refreshing: false,
        error: "Failed to refresh token",
      }));
      setTimeout(() => {
        setCanvaOAuth((prev) => ({ ...prev, error: null }));
      }, 5000);
    }
  };

  // Load Canva Services list
  const loadCanvaServices = async () => {
    try {
      setCanvaServices((prev) => ({ ...prev, loading: true, error: null }));

      const response = await authFetch(
        apiUrl(`/api/oauth/canva/services?user_id=${user?.id}`),
      );
      const data = await response.json();

      if (data.success) {
        setCanvaServices((prev) => ({
          ...prev,
          services: data.services || [],
          loading: false,
        }));
      } else {
        throw new Error(data.error || "Failed to load services");
      }
    } catch (error) {
      console.error("Failed to load Canva services:", error);
      setCanvaServices((prev) => ({
        ...prev,
        loading: false,
        error: "Failed to load services",
      }));
    }
  };

  // Toggle a Canva service on/off
  const handleToggleCanvaService = async (serviceId, currentlyEnabled) => {
    try {
      setCanvaServices((prev) => ({ ...prev, togglingService: serviceId }));

      if (currentlyEnabled) {
        // Disable service
        const response = await authFetch(
          apiUrl(`/api/oauth/canva/services/${serviceId}/disable?user_id=${user?.id}`),
          { method: "DELETE" },
        );
        const data = await response.json();

        if (data.success) {
          setCanvaServices((prev) => ({
            ...prev,
            services: prev.services.map((s) =>
              s.id === serviceId ? { ...s, enabled: false } : s,
            ),
            togglingService: null,
          }));
        } else {
          throw new Error(data.error || "Failed to disable service");
        }
      } else {
        // Enable service
        const response = await authFetch(
          apiUrl(`/api/oauth/canva/services/${serviceId}/enable?user_id=${user?.id}`),
          { method: "POST" },
        );
        const data = await response.json();

        if (data.success) {
          setCanvaServices((prev) => ({
            ...prev,
            services: prev.services.map((s) =>
              s.id === serviceId ? { ...s, enabled: true } : s,
            ),
            togglingService: null,
          }));
        } else if (data.needs_reauth) {
          // Need to re-authorize with additional scopes
          const authResponse = await authFetch(
            apiUrl(`/api/oauth/canva/authorize-incremental?user_id=${user?.id}&service_ids=${serviceId}`),
          );
          const authData = await authResponse.json();
          if (authData.authorization_url) {
            window.location.href = authData.authorization_url;
          } else {
            throw new Error("Failed to start authorization");
          }
        } else {
          throw new Error(data.error || "Failed to enable service");
        }
      }
    } catch (error) {
      console.error(`Failed to toggle Canva service ${serviceId}:`, error);
      setCanvaServices((prev) => ({
        ...prev,
        togglingService: null,
        error: error.message || "Failed to toggle service",
      }));
      setTimeout(() => {
        setCanvaServices((prev) => ({ ...prev, error: null }));
      }, 3000);
    }
  };

  // Connect Canva OAuth
  const handleCanvaConnect = async () => {
    try {
      setCanvaOAuth((prev) => ({ ...prev, connecting: true, error: null }));

      const response = await authFetch(
        apiUrl(`/api/oauth/canva/authorize?user_id=${user?.id}`),
      );
      const data = await response.json();

      if (data.authorization_url) {
        // Open in new window for better UX (user can see progress)
        const authWindow = window.open(data.authorization_url, '_blank', 'width=600,height=700');

        // Reset connecting state after short delay (window opened)
        setTimeout(() => {
          setCanvaOAuth((prev) => ({ ...prev, connecting: false }));
        }, 1000);

        // Poll for completion (window closed or redirected back)
        const pollInterval = setInterval(() => {
          if (authWindow?.closed) {
            clearInterval(pollInterval);
            // Refresh status after window closes
            checkCanvaOAuthStatus();
          }
        }, 500);

        // Clean up after 5 minutes max
        setTimeout(() => clearInterval(pollInterval), 5 * 60 * 1000);
      } else {
        throw new Error(data.error || "No authorization URL returned");
      }
    } catch (error) {
      console.error("Failed to initiate Canva OAuth:", error);
      setCanvaOAuth((prev) => ({
        ...prev,
        connecting: false,
        error: error.message || "Failed to start Canva connection",
      }));
    }
  };

  // Disconnect Canva OAuth
  const handleCanvaDisconnect = async () => {
    if (!confirm("Are you sure you want to disconnect your Canva account?")) {
      return;
    }

    try {
      setCanvaOAuth((prev) => ({ ...prev, disconnecting: true, error: null }));

      const response = await authFetch(
        apiUrl(`/api/oauth/canva/disconnect?user_id=${user?.id}`),
        { method: "DELETE" },
      );
      const data = await response.json();

      if (data.success) {
        setCanvaOAuth((prev) => ({
          ...prev,
          connected: false,
          scopes: [],
          expiresAt: null,
          disconnecting: false,
        }));
        setCanvaServices((prev) => ({ ...prev, services: [] }));
      } else {
        throw new Error(data.message || "Disconnect failed");
      }
    } catch (error) {
      console.error("Failed to disconnect Canva OAuth:", error);
      setCanvaOAuth((prev) => ({
        ...prev,
        disconnecting: false,
        error: "Failed to disconnect Canva account",
      }));
    }
  };

  // Fetch feature flags from backend
  const loadFeatureFlags = async () => {
    try {
      const response = await authFetch(apiUrl("/api/features"));
      const data = await response.json();
      if (data.success && data.features) {
        setFeatureFlags(data.features);
        console.log("[IntegrationsTab] Feature flags loaded", data.features);
      }
    } catch (error) {
      console.error("Failed to load feature flags:", error);
      // Default to empty - integrations with featureFlag won't show
      setFeatureFlags({});
    }
  };

  // Reload integrations when dynamic MCPs change
  useEffect(() => {
    if (dynamicMCPs.length > 0) {
      loadIntegrations();
    }
  }, [dynamicMCPs]);

  // Load published MCPs from Gateway
  const loadPublishedMCPs = async () => {
    try {
      const response = await authFetch(apiUrl("/api/mcp/published"));
      const data = await response.json();

      // OAuth MCPs require *_ACCESS_TOKEN env var - these are handled by OAuth provider cards
      const isOAuthMCP = (mcp) => {
        const envVars = mcp.env_vars_required || [];
        return envVars.some((v) => v.toUpperCase().endsWith("_ACCESS_TOKEN"));
      };

      const mcpIntegrations = (data.mcps || [])
        .filter((mcp) => !isOAuthMCP(mcp)) // Filter out OAuth MCPs
        .map((mcp) => ({
          id: mcp.name.toUpperCase().replace(/[\s-]/g, "_"),
          name: mcp.display_name,
          description: mcp.description || "No description",
          category: mcp.category || "other",
          authType: "api_key",
          icon: mcp.icon || "🔌",
          fields: (mcp.env_vars_required || []).map((envVar) => ({
            name: envVar,
            label: envVar.replace(/_/g, " "),
            type: "password",
            placeholder: `your-${envVar.toLowerCase().replace(/_/g, "-")}`,
            required: true,
          })),
          messages: {
            connecting: `Connecting to ${mcp.display_name}...`,
            testSuccess: `Successfully connected to ${mcp.display_name}!`,
            testFailure: "Connection failed. Please check your credentials.",
            saveSuccess: `${mcp.display_name} credentials saved successfully!`,
            saveFailure: "Failed to save credentials. Please try again.",
          },
          capabilities: [`${mcp.tool_count} tools available`],
          isDynamic: true,
          version: mcp.version,
        }));

      setDynamicMCPs(mcpIntegrations);
    } catch (error) {
      console.error("Failed to load published MCPs:", error);
      setDynamicMCPs([]);
    }
  };

  // Load user's custom tools
  const loadCustomTools = async () => {
    try {
      setCustomToolsLoading(true);
      const response = await authFetch(apiUrl("/api/mcp/my-tools"));
      const data = await response.json();
      setCustomTools(data.tools || []);
    } catch (error) {
      console.error("Failed to load custom tools:", error);
      setCustomTools([]);
    } finally {
      setCustomToolsLoading(false);
    }
  };

  // Poll for building tools
  useEffect(() => {
    const hasBuilding = customTools.some((t) => t.status === "building");
    if (!hasBuilding) return;

    const interval = setInterval(loadCustomTools, 15000);
    return () => clearInterval(interval);
  }, [customTools]);

  // Submit new custom tool build
  const handleCustomToolSubmit = async (e) => {
    e.preventDefault();
    setCustomToolError(null);
    setCustomToolSuccess(null);

    const name = (customToolForm.platform_name || "").trim();
    const creds = (customToolForm.credentials || []).filter(
      (c) => c.name.trim() && c.value.trim()
    );

    // Client-side validation with friendly messages
    if (!name) {
      setCustomToolError("Enter the name of the service (e.g., Asana, Basecamp, Notion).");
      return;
    }
    if (name.length < 2) {
      setCustomToolError("Service name is too short.");
      return;
    }
    if (creds.length === 0) {
      setCustomToolError("Add at least one credential. Not sure where to find it? Ask Ollie in chat.");
      return;
    }
    const shortCred = creds.find((c) => c.value.trim().length < 4);
    if (shortCred) {
      setCustomToolError(`"${shortCred.name}" looks too short — make sure you copied the full value.`);
      return;
    }

    try {
      setCustomToolSubmitting(true);
      const response = await authFetch(apiUrl("/api/mcp/my-tools"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          platform_name: name,
          credentials: creds.map((c) => ({ name: c.name.trim(), value: c.value.trim() })),
          // "spreadsheetId=1abc, projectId=42" → {spreadsheetId: "1abc", projectId: "42"}
          probe_samples: (customToolForm.probe_samples || "")
            .split(",")
            .map((pair) => pair.split("=").map((x) => x.trim()))
            .filter(([k, v]) => k && v)
            .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {}),
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        setCustomToolError(data.message || data.error || "Build request failed. Please try again.");
        return;
      }

      // Reset form and reload list
      const successName = name;
      setCustomToolForm({ platform_name: "", credentials: [{ name: "API_KEY", value: "" }], probe_samples: "" });
      setCustomToolSuccess(
        `Build started for ${successName}. It'll appear in the list below in a minute or two — status updates automatically.`
      );
      await loadCustomTools();
    } catch (error) {
      console.error("Custom tool submit error:", error);
      setCustomToolError("Connection error — please check your internet and try again.");
    } finally {
      setCustomToolSubmitting(false);
    }
  };

  // Delete a custom tool
  const handleCustomToolDelete = async (toolId, toolName) => {
    if (!confirm(`Delete custom tool "${toolName}"? This cannot be undone.`)) return;

    try {
      const response = await authFetch(apiUrl(`/api/mcp/my-tools/${toolId}`), {
        method: "DELETE",
      });
      const data = await response.json();

      if (data.success) {
        await loadCustomTools();
      } else {
        alert(data.error || "Delete failed");
      }
    } catch (error) {
      console.error("Custom tool delete error:", error);
      alert(error.message);
    }
  };

  // Update API key for a custom tool
  const handleCustomToolCredUpdate = async (toolId) => {
    const creds = (customToolCredValues[toolId] || []).filter(
      (c) => c.name.trim() && c.value.trim()
    );
    if (creds.length === 0) {
      alert("Add at least one credential.");
      return;
    }
    const shortCred = creds.find((c) => c.value.trim().length < 4);
    if (shortCred) {
      alert(`"${shortCred.name}" looks too short.`);
      return;
    }

    setCustomToolCredSaving((prev) => ({ ...prev, [toolId]: true }));
    try {
      const response = await authFetch(apiUrl(`/api/mcp/my-tools/${toolId}/credentials`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          credentials: creds.map((c) => ({ name: c.name.trim(), value: c.value.trim() })),
        }),
      });
      const data = await response.json();

      if (data.success) {
        setCustomToolCredEdit((prev) => ({ ...prev, [toolId]: false }));
        setCustomToolCredValues((prev) => ({ ...prev, [toolId]: undefined }));
      } else {
        alert(data.error || "Failed to update credentials");
      }
    } catch (error) {
      console.error("Credential update error:", error);
      alert("Connection error — please try again.");
    } finally {
      setCustomToolCredSaving((prev) => ({ ...prev, [toolId]: false }));
    }
  };

  // Load integration statuses from backend
  const loadIntegrations = async () => {
    setIsLoading(true);
    try {
      const response = await authFetch(apiUrl(`/api/credentials`));
      const data = await response.json();

      // Combine static and dynamic integrations (non-LLM)
      const allIntegrations = [...STATIC_INTEGRATIONS, ...dynamicMCPs];

      // Map backend data to our integration configs
      const integrationsWithStatus = allIntegrations.map((integration) => {
        const backendData =
          data.integrations?.find(
            (i) => i.id.toUpperCase() === integration.id,
          ) || {};

        return {
          ...integration,
          connected: backendData.connected || false,
          lastTested: backendData.lastTested || null,
          testStatus: backendData.testStatus || null,
        };
      });

      setIntegrations(integrationsWithStatus);

      // Also update LLM integrations status
      const llmWithStatus = LLM_INTEGRATIONS.map((llm) => {
        const backendData =
          data.integrations?.find((i) => i.id.toUpperCase() === llm.id) || {};

        return {
          ...llm,
          connected: backendData.connected || false,
          lastTested: backendData.lastTested || null,
          testStatus: backendData.testStatus || null,
        };
      });

      setLlmIntegrations(llmWithStatus);
    } catch (error) {
      console.error("Failed to load integrations:", error);
      const allIntegrations = [...STATIC_INTEGRATIONS, ...dynamicMCPs];
      setIntegrations(
        allIntegrations.map((i) => ({
          ...i,
          connected: false,
        })),
      );
    }
    setIsLoading(false);
  };

  // Open integration modal and load credentials
  const handleConnect = async (integration) => {
    setSelectedIntegration(integration);
    setTestResults(null);  // Use null instead of {} to properly clear test results
    setSelectedCredentials({});

    if (integration.connected) {
      setIsLoadingCredentials(true);
      try {
        const response = await authFetch(
          apiUrl(`/api/credentials/${integration.id.toLowerCase()}`),
        );
        const data = await response.json();

        if (data.success && data.credentials) {
          console.log(
            `[IntegrationsTab] Loaded credentials for ${integration.id}`,
            {
              hasCredentials: !!data.credentials,
              fields: Object.keys(data.credentials || {}),
            },
          );
          setSelectedCredentials(data.credentials);
        }
      } catch (error) {
        console.error(
          `Failed to load credentials for ${integration.name}:`,
          error,
        );
      } finally {
        setIsLoadingCredentials(false);
      }
    }
  };

  // Save credentials
  const handleSave = async (formData) => {
    if (!selectedIntegration) return false;

    setIsSaving(true);
    try {
      const response = await authFetch(
        apiUrl(`/api/credentials/${selectedIntegration.id.toLowerCase()}`),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ credentials: formData }),
        },
      );

      const result = await response.json();

      if (result.success) {
        // TEMPORARY BRIDGE: Also save to localStorage for legacy App.js
        if (
          selectedIntegration.id.toUpperCase() === "CLAUDE" &&
          formData.api_key
        ) {
          localStorage.setItem("claudeApiKey", formData.api_key);
          console.log(
            "[BYOK] Claude key synced to localStorage for legacy support",
          );
        }
        await loadIntegrations();
        return true;
      } else {
        alert(`❌ ${result.error || "Failed to save credentials"}`);
        return false;
      }
    } catch (error) {
      console.error("Save error:", error);
      alert(`❌ Failed to save credentials: ${error.message}`);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Test connection
  const handleTest = async (formData) => {
    if (!selectedIntegration) return;

    setIsTesting(true);
    setTestResults(null);

    try {
      const response = await authFetch(
        apiUrl(`/api/credentials/${selectedIntegration.id.toLowerCase()}/test`),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ credentials: formData }),
        },
      );

      const result = await response.json();

      setTestResults({
        success: result.success,
        error: result.error || result.message,
      });

      if (result.success) {
        await loadIntegrations();
      }
    } catch (error) {
      console.error("Test error:", error);
      setTestResults({
        success: false,
        error: error.message,
      });
    } finally {
      setIsTesting(false);
    }
  };

  // Test existing connection (from card)
  const handleTestExisting = async (integration) => {
    setIsTesting(true);
    try {
      const response = await authFetch(
        apiUrl(`/api/credentials/${integration.id.toLowerCase()}/test`),
        { method: "POST" },
      );

      const result = await response.json();

      if (result.success) {
        alert(`✅ ${integration.name} connection successful!`);
      } else {
        alert(
          `❌ ${integration.name} connection failed: ${result.error || "Unknown error"}`,
        );
      }

      await loadIntegrations();
    } catch (error) {
      console.error("Test error:", error);
      alert(`❌ Connection test failed: ${error.message}`);
    } finally {
      setIsTesting(false);
    }
  };

  // Delete credentials
  const handleDelete = async (integration) => {
    if (
      !window.confirm(
        `Are you sure you want to remove ${integration.name} credentials?`,
      )
    ) {
      return;
    }

    try {
      const response = await authFetch(
        apiUrl(`/api/credentials/${integration.id.toLowerCase()}`),
        { method: "DELETE" },
      );

      const result = await response.json();

      if (result.success) {
        // TEMPORARY BRIDGE: Also remove from localStorage for legacy App.js
        if (integration.id.toUpperCase() === "CLAUDE") {
          localStorage.removeItem("claudeApiKey");
          console.log("[BYOK] Claude key removed from localStorage");
        }
        alert(`✅ ${integration.name} credentials removed`);
        await loadIntegrations();
      }
    } catch (error) {
      console.error("Delete error:", error);
      alert(`❌ Failed to remove credentials: ${error.message}`);
    }
  };

  // Close modal
  const handleCloseModal = () => {
    setSelectedIntegration(null);
    setTestResults(null);
  };

  // State for Claude Max token input
  const [claudeMaxTokenInput, setClaudeMaxTokenInput] = useState("");
  const [overflowTokenInput, setOverflowTokenInput] = useState("");

  // Render LLM provider card (Claude, OpenAI, etc.)
  const renderLLMCard = (llm) => {
    const isComingSoon = llm.comingSoon;
    const isConnected = llm.isClaudeMax ? claudeMaxStatus.connected : llm.connected;

    // Special rendering for Claude Max
    if (llm.isClaudeMax) {
      // Create a modified integration object with correct connected status for the modal
      const claudeMaxIntegration = { ...llm, connected: isConnected };

      return (
        <div
          key={llm.id}
          className={`hf-panel hf-sheen hf-claude-hero${isConnected ? " hf-connected hf-glow-success" : ""}`}
          style={{
            border: isConnected ? "1px solid var(--border-success)" : "1px solid var(--border-primary)",
            borderRadius: "12px",
            padding: "20px",
            background: "var(--bg-tertiary)",
            transition: "all 0.2s ease",
            cursor: "default",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div>
              <h4
                style={{
                  margin: "0 0 8px 0",
                  color: "var(--text-primary)",
                  fontSize: "16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span>{llm.icon || "✨"}</span>
                {llm.name}
                {isConnected && (
                  <span
                    style={{
                      background: "var(--bg-success-subtle)",
                      color: "var(--accent-success)",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      fontSize: "11px",
                      fontWeight: "500",
                    }}
                  >
                    ✓ Connected
                  </span>
                )}
              </h4>
              <p
                style={{
                  margin: "0",
                  color: "var(--text-secondary)",
                  fontSize: "13px",
                }}
              >
                {llm.description}
              </p>
            </div>

            {isConnected && (
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={handleClaudeMaxTest}
                  disabled={claudeMaxStatus.loading}
                  style={{
                    padding: "6px 12px",
                    background: "transparent",
                    border: "1px solid var(--border-primary)",
                    borderRadius: "8px",
                    color: "var(--text-secondary)",
                    fontSize: "12px",
                    cursor: claudeMaxStatus.loading ? "not-allowed" : "pointer",
                  }}
                >
                  {claudeMaxStatus.loading ? "Testing..." : "Test"}
                </button>
                <button
                  onClick={handleClaudeMaxDisconnect}
                  disabled={claudeMaxStatus.loading}
                  style={{
                    padding: "6px 12px",
                    background: "transparent",
                    border: "1px solid var(--border-danger)",
                    borderRadius: "8px",
                    color: "var(--accent-danger)",
                    fontSize: "12px",
                    cursor: claudeMaxStatus.loading ? "not-allowed" : "pointer",
                  }}
                >
                  Disconnect
                </button>
              </div>
            )}
          </div>


          {/* Token Input when NOT connected */}
          {!isConnected && (
            <div style={{ marginTop: "16px" }}>
              <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                <input
                  type="password"
                  placeholder="sk-ant-oat01-..."
                  value={claudeMaxTokenInput}
                  onChange={(e) => setClaudeMaxTokenInput(e.target.value)}
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    background: "var(--bg-input)",
                    border: "1px solid var(--border-secondary)",
                    borderRadius: "8px",
                    color: "var(--text-primary)",
                    fontSize: "13px",
                  }}
                />
                <button
                  onClick={async () => {
                    // Remove all whitespace (including newlines from copy/paste)
                    const cleanToken = claudeMaxTokenInput.replace(/\s+/g, "");
                    if (cleanToken) {
                      const success = await handleClaudeMaxConnect(cleanToken);
                      if (success) {
                        setClaudeMaxTokenInput("");
                      }
                    }
                  }}
                  disabled={claudeMaxStatus.loading || !claudeMaxTokenInput.replace(/\s+/g, "")}
                  className="hf-press hf-focus"
                  style={{
                    padding: "10px 20px",
                    background: claudeMaxStatus.loading || !claudeMaxTokenInput.trim() ? "var(--text-tertiary)" : "var(--accent-primary)",
                    border: "none",
                    borderRadius: "8px",
                    color: "var(--text-on-accent)",
                    fontSize: "13px",
                    fontWeight: "500",
                    cursor: claudeMaxStatus.loading || !claudeMaxTokenInput.trim() ? "not-allowed" : "pointer",
                  }}
                >
                  {claudeMaxStatus.loading ? "Connecting..." : "Connect"}
                </button>
              </div>

              {/* Setup Instructions */}
              <div
                style={{
                  background: "var(--bg-primary)",
                  borderRadius: "8px",
                  padding: "12px 16px",
                  border: "1px solid var(--border-primary)",
                }}
              >
                <p style={{ margin: "0 0 8px 0", color: "var(--text-secondary)", fontSize: "12px", fontWeight: "500" }}>
                  How to get a setup token:
                </p>
                <ol style={{ margin: 0, paddingLeft: "20px", color: "var(--text-secondary)", fontSize: "12px", lineHeight: "1.6" }}>
                  {llm.setupInstructions?.map((instruction, i) => (
                    <li key={i} style={{ marginBottom: "4px" }}>{instruction.replace(/^\d+\.\s*/, "")}</li>
                  ))}
                </ol>
              </div>
            </div>
          )}

          {llm.helpText && !isConnected && (
            <small
              style={{
                display: "block",
                marginTop: "12px",
                color: "var(--text-secondary)",
                fontSize: "12px",
              }}
            >
              {llm.helpText}
            </small>
          )}

          {/* Overflow Claude token (claude_overflow_failover) — optional
              second token that automatically takes over when the primary
              plan hits its usage limit (plan-limit 429). */}
          {claudeOverflowEnabled && isConnected && (
            <div
              style={{
                marginTop: "16px",
                paddingTop: "16px",
                borderTop: "1px solid var(--border-primary)",
              }}
            >
              <p style={{ margin: "0 0 4px 0", color: "var(--text-primary)", fontSize: "13px", fontWeight: "500" }}>
                Overflow Claude token (optional)
              </p>
              <p style={{ margin: "0 0 10px 0", color: "var(--text-secondary)", fontSize: "12px" }}>
                A second Claude token that automatically takes over ONLY when your
                primary plan hits its usage limit — you'll see a notice whenever it's
                serving. Same encrypted storage as your primary token.
              </p>

              {claudeMaxStatus.overflow?.connected ? (
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span
                      style={{
                        background: claudeMaxStatus.overflow.lastError ? "var(--bg-danger-subtle)" : "var(--bg-success-subtle)",
                        color: claudeMaxStatus.overflow.lastError ? "var(--accent-danger)" : "var(--accent-success)",
                        padding: "2px 8px",
                        borderRadius: "12px",
                        fontSize: "11px",
                        fontWeight: "500",
                      }}
                    >
                      {claudeMaxStatus.overflow.lastError ? "⚠ Needs attention" : "✓ Overflow ready"}
                    </span>
                    <button
                      onClick={handleOverflowRemove}
                      disabled={claudeMaxStatus.loading}
                      style={{
                        padding: "4px 10px",
                        background: "transparent",
                        border: "1px solid var(--border-danger)",
                        borderRadius: "8px",
                        color: "var(--accent-danger)",
                        fontSize: "12px",
                        cursor: claudeMaxStatus.loading ? "not-allowed" : "pointer",
                      }}
                    >
                      Remove
                    </button>
                  </div>
                  <div style={{ marginTop: "6px", color: "var(--text-secondary)", fontSize: "11px" }}>
                    {claudeMaxStatus.overflow.lastError
                      ? `Last overflow use failed: ${claudeMaxStatus.overflow.lastError} — if it expired, paste a fresh token.`
                      : claudeMaxStatus.overflow.lastOkAt
                        ? `Last served: ${new Date(claudeMaxStatus.overflow.lastOkAt).toLocaleString()}`
                        : "Standing by — hasn't been needed yet."}
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", gap: "8px" }}>
                  <input
                    type="password"
                    placeholder="sk-ant-oat01-... (overflow)"
                    value={overflowTokenInput}
                    onChange={(e) => setOverflowTokenInput(e.target.value)}
                    style={{
                      flex: 1,
                      padding: "10px 12px",
                      background: "var(--bg-input)",
                      border: "1px solid var(--border-secondary)",
                      borderRadius: "8px",
                      color: "var(--text-primary)",
                      fontSize: "13px",
                    }}
                  />
                  <button
                    onClick={async () => {
                      const cleanToken = overflowTokenInput.replace(/\s+/g, "");
                      if (cleanToken) {
                        const success = await handleOverflowConnect(cleanToken);
                        if (success) setOverflowTokenInput("");
                      }
                    }}
                    disabled={claudeMaxStatus.loading || !overflowTokenInput.replace(/\s+/g, "")}
                    className="hf-press hf-focus"
                    style={{
                      padding: "10px 20px",
                      background: claudeMaxStatus.loading || !overflowTokenInput.trim() ? "var(--text-tertiary)" : "var(--accent-primary)",
                      border: "none",
                      borderRadius: "8px",
                      color: "var(--text-on-accent)",
                      fontSize: "13px",
                      fontWeight: "500",
                      cursor: claudeMaxStatus.loading || !overflowTokenInput.trim() ? "not-allowed" : "pointer",
                    }}
                  >
                    Save
                  </button>
                </div>
              )}
            </div>
          )}

          {claudeMaxStatus.error && (
            <div
              style={{
                marginTop: "12px",
                padding: "8px 12px",
                background: "var(--bg-danger-subtle)",
                border: "1px solid var(--border-danger)",
                borderRadius: "8px",
                color: "var(--accent-danger)",
                fontSize: "12px",
              }}
            >
              ❌ {claudeMaxStatus.error}
            </div>
          )}
        </div>
      );
    }

    // Standard LLM card rendering (Claude API, etc.)
    return (
      <div
        key={llm.id}
        className={`hf-panel hf-lift${isConnected ? " hf-connected" : ""}`}
        style={{
          border: isConnected ? "1px solid var(--border-success)" : "1px solid var(--border-primary)",
          borderRadius: "12px",
          padding: "20px",
          background: "var(--bg-tertiary)",
          opacity: isComingSoon ? 0.6 : 1,
          cursor: isComingSoon ? "not-allowed" : "pointer",
          transition: "all 0.2s ease",
        }}
        onClick={() => !isComingSoon && handleConnect(llm)}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <div>
            <h4
              style={{
                margin: "0 0 8px 0",
                color: "var(--text-primary)",
                fontSize: "16px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span>{llm.icon || "🤖"}</span>
              {llm.name}
              {isConnected && (
                <span
                  style={{
                    background: "var(--bg-success-subtle)",
                    color: "var(--accent-success)",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    fontSize: "11px",
                    fontWeight: "500",
                  }}
                >
                  ✓ Connected
                </span>
              )}
            </h4>
            <p
              style={{
                margin: "0",
                color: "var(--text-secondary)",
                fontSize: "13px",
              }}
            >
              {isComingSoon ? "Coming soon" : llm.description}
            </p>
          </div>

          {!isComingSoon && (
            <div style={{ display: "flex", gap: "8px" }}>
              {isConnected && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTestExisting(llm);
                    }}
                    disabled={isTesting}
                    style={{
                      padding: "6px 12px",
                      background: "transparent",
                      border: "1px solid var(--border-primary)",
                      borderRadius: "8px",
                      color: "var(--text-secondary)",
                      fontSize: "12px",
                      cursor: "pointer",
                    }}
                  >
                    {isTesting ? "Testing..." : "Test"}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(llm);
                    }}
                    style={{
                      padding: "6px 12px",
                      background: "transparent",
                      border: "1px solid var(--border-danger)",
                      borderRadius: "8px",
                      color: "var(--accent-danger)",
                      fontSize: "12px",
                      cursor: "pointer",
                    }}
                  >
                    Remove
                  </button>
                </>
              )}
              {!isConnected && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleConnect(llm);
                  }}
                  className="hf-press hf-focus"
                  style={{
                    padding: "6px 16px",
                    background: "var(--accent-primary)",
                    border: "none",
                    borderRadius: "8px",
                    color: "var(--text-on-accent)",
                    fontSize: "12px",
                    fontWeight: "500",
                    cursor: "pointer",
                  }}
                >
                  Add Key
                </button>
              )}
            </div>
          )}
        </div>

        {llm.helpText && !isComingSoon && (
          <small
            style={{
              display: "block",
              marginTop: "12px",
              color: "var(--text-secondary)",
              fontSize: "12px",
            }}
          >
            {llm.helpText}
          </small>
        )}

        {llm.rateLimitWarning && !isComingSoon && (
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "8px",
              marginTop: "12px",
              padding: "10px 12px",
              background: "var(--bg-warning-subtle)",
              border: "1px solid var(--border-warning)",
              borderRadius: "8px",
            }}
          >
            <span style={{ fontSize: "14px" }}>⚠️</span>
            <div style={{ flex: 1 }}>
              <span
                style={{
                  color: "var(--accent-warning)",
                  fontSize: "12px",
                  lineHeight: "1.4",
                }}
              >
                {llm.rateLimitWarning}
              </span>
              {llm.rateLimitDetails && (
                <div
                  style={{
                    position: "relative",
                    display: "inline-block",
                    marginLeft: "6px",
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "16px",
                      height: "16px",
                      borderRadius: "50%",
                      background: "var(--text-tertiary)",
                      color: "var(--text-tertiary)",
                      fontSize: "11px",
                      fontWeight: "bold",
                      cursor: "help",
                    }}
                    title={llm.rateLimitDetails}
                  >
                    i
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {isConnected && llm.lastTested && (
          <small
            style={{
              display: "block",
              marginTop: "8px",
              color: "var(--text-secondary)",
              fontSize: "11px",
            }}
          >
            Last tested: {new Date(llm.lastTested).toLocaleString()}
            {llm.testStatus === "success" ? " ✓" : " ✗"}
          </small>
        )}
      </div>
    );
  };

  return (
    <div className="settings-section">
      {/* Claude first — connecting your Claude is THE prerequisite for
          everything else in Optimus (Brad 2026-08-21: "claude card should be
          the first thing on that menu"). */}
      {canUseBYOK && (
        <div style={{ marginBottom: "32px" }}>
          <h3>🤖 AI Provider Keys<HelpDot term="byok" label="What is BYOK?" /></h3>
          <p style={{ color: "var(--text-secondary)", marginBottom: "24px", fontSize: "14px" }}>
            Bring your own API keys - pay only for what you use, no platform fees
          </p>
          <div className="hf-stagger" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {llmIntegrations.map((llm) => renderLLMCard(llm))}
          </div>
        </div>
      )}

      {/* Surfaces — Optimus on the go (Mako) + on the desktop (Orca).
          Integrations is only for connecting things (platforms, Mako, Orca,
          MCPs). Automations (incl. Harry event webhooks) live in the
          Automations menu, built for you by Ollie from what you want. */}
      <MakoConnectCard />
      <MakoTextCard />
      <OrcaConnectCard />
      <GithubBackupCard />

      {/* API Key Rotation Reminder Banner */}
      <div
        style={{
          background: "var(--bg-tertiary)",
          padding: "12px 16px",
          borderRadius: "8px",
          marginBottom: "20px",
          borderLeft: "4px solid var(--accent-warning)",
        }}
      >
        <p style={{ margin: 0, fontSize: "14px", color: "var(--text-secondary)" }}>
          🔐 <strong style={{ color: "var(--text-primary)" }}>Security Tip:</strong> We
          recommend rotating your API keys every 90 days. Optimus stores your
          keys encrypted but cannot enforce rotation on external services.
        </p>
      </div>

      {/* OAuth Connections Section */}
      <div
        style={{
          paddingTop: "24px",
          paddingBottom: "24px",
          borderTop: "1px solid var(--border-primary)",
          marginBottom: "24px",
        }}
      >
        <h3>🔐 OAuth Connections<HelpDot term="oauth" label="OAuth vs API key?" /></h3>
        <p style={{ color: "var(--text-secondary)", marginBottom: "24px", fontSize: "14px" }}>
          Connect your accounts to switch on the built-in connections that need OAuth.
          Heads up: a connection only works as far as your key's scopes reach —
          if your key is missing a scope, some of its tools won't work. Want a
          connection matched 1:1 to exactly the scopes your key has? Build a
          Custom MCP instead — it form-fits to your key — and ask Ollie if you need help.
        </p>

        {/* Google OAuth Card */}
        <div
          className={`hf-panel hf-lift${googleOAuth.connected ? " hf-connected" : ""}`}
          style={{
            border: googleOAuth.connected
              ? "1px solid var(--border-success)"
              : "1px solid var(--border-primary)",
            borderRadius: "12px",
            padding: "20px",
            background: "var(--bg-tertiary)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div>
              <h4
                style={{
                  margin: "0 0 8px 0",
                  color: "var(--text-primary)",
                  fontSize: "16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span>🔵</span>
                Google
                {googleOAuth.connected && (
                  <span
                    style={{
                      background: "var(--bg-success-subtle)",
                      color: "var(--accent-success)",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      fontSize: "11px",
                      fontWeight: "500",
                    }}
                  >
                    ✓ Connected
                  </span>
                )}
              </h4>
              <p
                style={{
                  margin: "0",
                  color: "var(--text-secondary)",
                  fontSize: "13px",
                }}
              >
                Connect your Google account to switch on the built-in Google connections
                (Drive, Calendar, Gmail, etc.)
              </p>
            </div>

            <div style={{ display: "flex", gap: "8px" }}>
              {googleOAuth.loading ? (
                <span style={{ color: "var(--text-secondary)", fontSize: "12px" }}>
                  Loading...
                </span>
              ) : googleOAuth.connected ? (
                <button
                  onClick={handleGoogleDisconnect}
                  disabled={googleOAuth.disconnecting}
                  style={{
                    padding: "6px 12px",
                    background: "transparent",
                    border: "1px solid var(--border-danger)",
                    borderRadius: "8px",
                    color: "var(--accent-danger)",
                    fontSize: "12px",
                    cursor: googleOAuth.disconnecting
                      ? "not-allowed"
                      : "pointer",
                    opacity: googleOAuth.disconnecting ? 0.6 : 1,
                  }}
                >
                  {googleOAuth.disconnecting
                    ? "Disconnecting..."
                    : "Disconnect"}
                </button>
              ) : (
                <button
                  onClick={handleGoogleConnect}
                  disabled={googleOAuth.connecting}
                  className="hf-press hf-focus"
                  style={{
                    padding: "6px 16px",
                    background: "#4285f4",
                    border: "none",
                    borderRadius: "8px",
                    color: "var(--text-primary)",
                    fontSize: "12px",
                    fontWeight: "500",
                    cursor: googleOAuth.connecting ? "not-allowed" : "pointer",
                    opacity: googleOAuth.connecting ? 0.6 : 1,
                  }}
                >
                  {googleOAuth.connecting ? "Connecting..." : "Connect Google"}
                </button>
              )}
            </div>
          </div>

          {/* Loud pre-connect warning — Google's "unverified app" screen
              is a multi-click horror show (Advanced → Continue → Are
              you sure → Confirm). Users who skim don't finish. Making
              this LOUD (yellow, big, first-thing-you-see) so it can't
              be missed. Softens the friction Brad hit walking the
              path 2026-08-26. Fully deletable once Google finishes
              full sensitive-scope verification (deferred until ~80
              users). Renders only in the pre-connect state. */}
          {!googleOAuth.loading && !googleOAuth.connected && (
            <div
              style={{
                marginTop: "14px",
                padding: "16px 18px",
                background: "var(--bg-warning-subtle)",
                border: "2px solid var(--accent-warning)",
                borderRadius: "10px",
                color: "var(--text-primary)",
                fontSize: "14px",
                lineHeight: 1.6,
              }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                <span style={{ fontSize: "22px", flexShrink: 0, lineHeight: 1 }}>⚠️</span>
                <div>
                  <div style={{ fontWeight: 800, fontSize: "15px", marginBottom: "8px", letterSpacing: "0.02em", textTransform: "uppercase" }}>
                    Before you click the button to connect to Google, read this
                  </div>
                  <div style={{ marginBottom: "8px" }}>
                    Once you click Connect Google, Google will say{" "}
                    <strong>&ldquo;Google hasn&rsquo;t verified this app&rdquo;</strong>
                    {" "}and hide the sign-in button. This is normal — we&rsquo;re still
                    finishing Google&rsquo;s verification review.
                  </div>
                  <div style={{ marginBottom: "8px" }}>
                    <strong>To get through:</strong>
                  </div>
                  <ol style={{ margin: "0 0 8px 0", paddingLeft: "20px" }}>
                    <li>Click <strong>&ldquo;Advanced&rdquo;</strong> (small text, bottom-left)</li>
                    <li>Click <strong>&ldquo;Go to Optimus OS (unsafe)&rdquo;</strong></li>
                    <li>Grant the scopes you want</li>
                  </ol>
                  <div style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
                    You&rsquo;re not going anywhere unsafe — &ldquo;unsafe&rdquo; is Google&rsquo;s
                    default label for every app that hasn&rsquo;t finished verification yet.
                    You&rsquo;ll only see this once.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error message */}
          {googleOAuth.error && (
            <div
              style={{
                marginTop: "12px",
                padding: "8px 12px",
                background: "var(--bg-danger-subtle)",
                border: "1px solid var(--border-danger)",
                borderRadius: "8px",
                color: "var(--accent-danger)",
                fontSize: "12px",
              }}
            >
              ❌ {googleOAuth.error}
            </div>
          )}

          {/* Connected info with token health */}
          {googleOAuth.connected && (
            <div
              style={{
                marginTop: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  flexWrap: "wrap",
                }}
              >
                {/* Token Health Indicator */}
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontSize: "11px",
                    fontWeight: "500",
                    background:
                      googleOAuth.tokenHealth === "healthy"
                        ? "var(--bg-success-subtle)"
                        : googleOAuth.tokenHealth === "expiring_soon"
                          ? "var(--bg-warning-subtle)"
                          : googleOAuth.tokenHealth === "needs_refresh"
                            ? "var(--bg-warning-subtle)"
                            : googleOAuth.tokenHealth === "expired"
                              ? "var(--bg-danger-subtle)"
                              : "var(--bg-tertiary)",
                    color:
                      googleOAuth.tokenHealth === "healthy"
                        ? "var(--accent-success)"
                        : googleOAuth.tokenHealth === "expiring_soon"
                          ? "var(--accent-warning)"
                          : googleOAuth.tokenHealth === "needs_refresh"
                            ? "var(--accent-warning)"
                            : googleOAuth.tokenHealth === "expired"
                              ? "var(--accent-danger)"
                              : "var(--text-secondary)",
                  }}
                >
                  <span style={{ fontSize: "8px" }}>
                    {googleOAuth.tokenHealth === "healthy"
                      ? "●"
                      : googleOAuth.tokenHealth === "expired"
                        ? "○"
                        : "◐"}
                  </span>
                  {googleOAuth.tokenHealth === "healthy"
                    ? "Token Healthy"
                    : googleOAuth.tokenHealth === "expiring_soon"
                      ? `Expiring in ${googleOAuth.minutesUntilExpiry}m`
                      : googleOAuth.tokenHealth === "needs_refresh"
                        ? "Needs Refresh"
                        : googleOAuth.tokenHealth === "expired"
                          ? "Token Expired"
                          : "Checking..."}
                </span>

                {googleOAuth.scopes?.length > 0 && (
                  <span style={{ color: "var(--text-secondary)", fontSize: "11px" }}>
                    • {googleOAuth.scopes.length} scopes granted
                  </span>
                )}
              </div>

              {/* Refresh Button */}
              {(googleOAuth.tokenHealth === "needs_refresh" ||
                googleOAuth.tokenHealth === "expiring_soon" ||
                googleOAuth.tokenHealth === "expired") && (
                <button
                  onClick={handleRefreshToken}
                  disabled={googleOAuth.refreshing}
                  style={{
                    padding: "4px 10px",
                    background: googleOAuth.refreshing ? "var(--text-tertiary)" : "var(--accent-primary)",
                    border: "none",
                    borderRadius: "4px",
                    color: "var(--text-on-accent)",
                    fontSize: "11px",
                    fontWeight: "500",
                    cursor: googleOAuth.refreshing ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  {googleOAuth.refreshing ? (
                    <>
                      <span
                        style={{
                          display: "inline-block",
                          width: "10px",
                          height: "10px",
                          border: "2px solid var(--border-primary)",
                          borderTopColor: "var(--text-on-accent)",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                        }}
                      />
                      Refreshing...
                    </>
                  ) : (
                    <>🔄 Refresh Token</>
                  )}
                </button>
              )}
            </div>
          )}

          {/* Google Services Menu - Shows when connected */}
          {googleOAuth.connected && (
            <div
              style={{
                marginTop: "20px",
                paddingTop: "20px",
                borderTop: "1px solid var(--border-primary)",
              }}
            >
              <h5
                style={{
                  margin: "0 0 12px 0",
                  color: "var(--text-primary)",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                🛠️ Enable Google Services
              </h5>
              <p
                style={{
                  margin: "0 0 16px 0",
                  color: "var(--text-secondary)",
                  fontSize: "12px",
                }}
              >
                Toggle which Google services Ollie can act on. Enable a service
                and its tools switch on.
              </p>

              {/* Services error */}
              {googleServices.error && (
                <div
                  style={{
                    marginBottom: "12px",
                    padding: "8px 12px",
                    background: "var(--bg-danger-subtle)",
                    border: "1px solid var(--border-danger)",
                    borderRadius: "8px",
                    color: "var(--accent-danger)",
                    fontSize: "12px",
                  }}
                >
                  ❌ {googleServices.error}
                </div>
              )}

              {/* Bulk Auth Mode Toggle & Actions */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                  padding: "12px",
                  background: bulkAuthMode ? "var(--bg-accent-subtle)" : "var(--bg-tertiary)",
                  border: bulkAuthMode
                    ? "1px solid var(--accent-primary)"
                    : "1px solid var(--border-primary)",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    onClick={() => {
                      if (bulkAuthMode) {
                        handleCancelBulkAuth();
                      } else {
                        setBulkAuthMode(true);
                      }
                    }}
                    style={{
                      padding: "8px 16px",
                      background: bulkAuthMode ? "var(--accent-danger)" : "var(--accent-primary)",
                      border: "none",
                      borderRadius: "8px",
                      color: "var(--text-on-accent)",
                      fontSize: "13px",
                      fontWeight: "500",
                      cursor: "pointer",
                    }}
                  >
                    {bulkAuthMode
                      ? "✕ Cancel Bulk Mode"
                      : "⚡ Bulk Enable Mode"}
                  </button>
                  {bulkAuthMode && (
                    <>
                      <button
                        onClick={() => {
                          // Select all non-enabled services (excluding AC)
                          const allDisabledServices = googleServices.services
                            .filter(
                              (s) =>
                                !s.enabled &&
                                s.id !== "GOOGLE_AC" &&
                                !s.id.includes("_AC"),
                            )
                            .map((s) => s.id);
                          setPendingServices(allDisabledServices);
                        }}
                        style={{
                          padding: "6px 12px",
                          background: "var(--accent-primary)",
                          border: "none",
                          borderRadius: "4px",
                          color: "var(--text-on-accent)",
                          fontSize: "12px",
                          fontWeight: "500",
                          cursor: "pointer",
                        }}
                      >
                        Select All
                      </button>
                      <button
                        onClick={() => {
                          setPendingServices([]);
                        }}
                        style={{
                          padding: "6px 12px",
                          background: "var(--bg-tertiary)",
                          border: "none",
                          borderRadius: "4px",
                          color: "var(--text-primary)",
                          fontSize: "12px",
                          fontWeight: "500",
                          cursor: "pointer",
                        }}
                      >
                        Select None
                      </button>
                      <span style={{ color: "var(--text-secondary)", fontSize: "12px" }}>
                        Select services, then authorize all at once
                      </span>
                    </>
                  )}
                </div>

                {bulkAuthMode && pendingServices.length > 0 && (
                  <button
                    onClick={handleBulkAuth}
                    disabled={bulkAuthLoading}
                    style={{
                      padding: "10px 20px",
                      background: bulkAuthLoading ? "var(--text-tertiary)" : "var(--accent-success)",
                      border: "none",
                      borderRadius: "8px",
                      color: "var(--text-on-accent)",
                      fontSize: "13px",
                      fontWeight: "600",
                      cursor: bulkAuthLoading ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {bulkAuthLoading ? (
                      <>
                        <span
                          style={{
                            display: "inline-block",
                            width: "14px",
                            height: "14px",
                            border: "2px solid var(--text-on-accent)",
                            borderTopColor: "transparent",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                          }}
                        />
                        Authorizing...
                      </>
                    ) : (
                      <>
                        🔐 Authorize {pendingServices.length} Service
                        {pendingServices.length > 1 ? "s" : ""}
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* Services list */}
              {googleServices.loading ? (
                <div style={{ color: "var(--text-secondary)", fontSize: "12px" }}>
                  Loading services...
                </div>
              ) : (
                <div
                  className="hf-stagger"
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(220px, 1fr))",
                    gap: "12px",
                  }}
                >
                  {googleServices.services.map((service) => {
                    const isToggling =
                      googleServices.togglingService === service.id;
                    const isPending = pendingServices.includes(service.id);
                    const serviceIcons = {
                      GOOGLE_DRIVE: "📁",
                      GOOGLE_CALENDAR: "📅",
                      GOOGLE_IAM: "🔐",
                      GOOGLE_SHEETS: "📊",
                      GOOGLE_DOCS: "📝",
                      GOOGLE_GMAIL: "📧",
                      GOOGLE_TASKS: "✅",
                      GOOGLE_CONTACTS: "👥",
                      GOOGLE_YOUTUBE: "🎬",
                      GOOGLE_CLOUD: "☁️",
                      GOOGLE_BIGQUERY: "📊",
                      GOOGLE_SEARCH_CONSOLE: "🔍",
                      GOOGLE_ANALYTICS_CONSOLE: "📈",
                      GOOGLE_ANALYTICS_DATA: "📈",
                      GOOGLE_FORMS: "📋",
                      GOOGLE_SLIDES: "🎞️",
                      GOOGLE_PHOTOS: "📷",
                      GOOGLE_PHOTOSLIBRARY: "📷",
                      GOOGLE_WALLETOBJECTS: "💳",
                      GOOGLE_YOUTUBEREPORTING: "📊",
                      GOOGLE_YOUTUBE_ANALYTICS: "📊",
                    };

                    // Determine visual state
                    const isActive = service.enabled || isPending;
                    const borderColor = service.enabled
                      ? "var(--border-success)"
                      : isPending
                        ? "var(--border-accent)"
                        : "var(--border-secondary)";
                    const bgColor = service.enabled
                      ? "var(--bg-success-subtle)"
                      : isPending
                        ? "var(--bg-accent-subtle)"
                        : "var(--bg-tertiary)";

                    return (
                      <div
                        key={service.id}
                        onClick={() => {
                          if (bulkAuthMode && !service.enabled) {
                            handleTogglePendingService(service.id);
                          }
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "12px",
                          background: bgColor,
                          border: `1px solid ${borderColor}`,
                          borderRadius: "8px",
                          transition: "all 0.2s ease",
                          cursor:
                            bulkAuthMode && !service.enabled
                              ? "pointer"
                              : "default",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          {/* Checkbox for bulk mode */}
                          {bulkAuthMode && !service.enabled && (
                            <div
                              style={{
                                width: "18px",
                                height: "18px",
                                borderRadius: "4px",
                                border: isPending
                                  ? "none"
                                  : "2px solid var(--text-tertiary)",
                                background: isPending
                                  ? "var(--accent-primary)"
                                  : "transparent",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "all 0.2s ease",
                              }}
                            >
                              {isPending && (
                                <span
                                  style={{
                                    color: "var(--text-on-accent)",
                                    fontSize: "12px",
                                    fontWeight: "bold",
                                  }}
                                >
                                  ✓
                                </span>
                              )}
                            </div>
                          )}
                          <span style={{ fontSize: "16px" }}>
                            {serviceIcons[service.id] || "⚙️"}
                          </span>
                          <span
                            style={{
                              color: "var(--text-primary)",
                              fontSize: "13px",
                              fontWeight: "500",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "120px",
                            }}
                            title={service.name}
                          >
                            {service.name}
                          </span>
                          {isPending && !service.enabled && (
                            <span
                              style={{
                                fontSize: "10px",
                                padding: "2px 6px",
                                background: "var(--bg-accent-subtle)",
                                color: "var(--accent-primary)",
                                borderRadius: "4px",
                                fontWeight: "500",
                              }}
                            >
                              PENDING
                            </span>
                          )}
                        </div>

                        {/* Toggle Switch - hidden in bulk mode for non-enabled services */}
                        {(!bulkAuthMode || service.enabled) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleGoogleService(
                                service.id,
                                service.enabled,
                              );
                            }}
                            disabled={isToggling}
                            style={{
                              position: "relative",
                              width: "44px",
                              height: "24px",
                              borderRadius: "12px",
                              border: "none",
                              background: service.enabled
                                ? "var(--accent-success)"
                                : "var(--text-tertiary)",
                              cursor: isToggling ? "not-allowed" : "pointer",
                              transition: "background 0.2s ease",
                              opacity: isToggling ? 0.6 : 1,
                            }}
                            title={
                              service.enabled
                                ? `Disable ${service.name}`
                                : `Enable ${service.name}`
                            }
                          >
                            <span
                              style={{
                                position: "absolute",
                                top: "2px",
                                left: service.enabled ? "22px" : "2px",
                                width: "20px",
                                height: "20px",
                                borderRadius: "50%",
                                background: "#ffffff",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                                transition: "left 0.2s ease",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "10px",
                                color: "var(--text-secondary)",
                              }}
                            >
                              {isToggling ? "..." : ""}
                            </span>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Help text */}
              <small
                style={{
                  display: "block",
                  marginTop: "16px",
                  color: "var(--text-secondary)",
                  fontSize: "11px",
                }}
              >
                💡 Tip: Use <strong>Bulk Enable Mode</strong> to select multiple
                services and authorize them all with a single Google sign-in.
                Much faster than enabling one at a time!
              </small>
            </div>
          )}
        </div>

        {/* Canva OAuth Card - Feature flagged */}
        {canvaEnabled && (
        <div
          className={`hf-panel hf-lift${canvaOAuth.connected ? " hf-connected" : ""}`}
          style={{
            border: canvaOAuth.connected
              ? "1px solid var(--border-success)"
              : "1px solid var(--border-primary)",
            borderRadius: "12px",
            padding: "20px",
            marginTop: "16px",
            background: "var(--bg-tertiary)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div>
              <h4
                style={{
                  margin: "0 0 8px 0",
                  color: "var(--text-primary)",
                  fontSize: "16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span>🎨</span>
                Canva
                {canvaOAuth.connected && (
                  <span
                    style={{
                      background: "var(--bg-success-subtle)",
                      color: "var(--accent-success)",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      fontSize: "11px",
                      fontWeight: "500",
                    }}
                  >
                    ✓ Connected
                  </span>
                )}
              </h4>
              <p
                style={{
                  margin: "0",
                  color: "var(--text-secondary)",
                  fontSize: "13px",
                }}
              >
                Connect your Canva account to create and manage designs, assets, and brand templates
              </p>
            </div>

            <div style={{ display: "flex", gap: "8px" }}>
              {canvaOAuth.loading ? (
                <span style={{ color: "var(--text-secondary)", fontSize: "12px" }}>
                  Loading...
                </span>
              ) : canvaOAuth.connected ? (
                <button
                  onClick={handleCanvaDisconnect}
                  disabled={canvaOAuth.disconnecting}
                  style={{
                    padding: "6px 12px",
                    background: "transparent",
                    border: "1px solid var(--border-danger)",
                    borderRadius: "8px",
                    color: "var(--accent-danger)",
                    fontSize: "12px",
                    cursor: canvaOAuth.disconnecting ? "not-allowed" : "pointer",
                    opacity: canvaOAuth.disconnecting ? 0.6 : 1,
                  }}
                >
                  {canvaOAuth.disconnecting ? "Disconnecting..." : "Disconnect"}
                </button>
              ) : (
                <button
                  onClick={handleCanvaConnect}
                  disabled={canvaOAuth.connecting}
                  className="hf-press hf-focus"
                  style={{
                    padding: "6px 16px",
                    background: "#7B2CBF",
                    border: "none",
                    borderRadius: "8px",
                    color: "var(--text-primary)",
                    fontSize: "12px",
                    fontWeight: "500",
                    cursor: canvaOAuth.connecting ? "not-allowed" : "pointer",
                    opacity: canvaOAuth.connecting ? 0.6 : 1,
                  }}
                >
                  {canvaOAuth.connecting ? "Connecting..." : "Connect Canva"}
                </button>
              )}
            </div>
          </div>

          {/* Error message */}
          {canvaOAuth.error && (
            <div
              style={{
                marginTop: "12px",
                padding: "8px 12px",
                background: "var(--bg-danger-subtle)",
                border: "1px solid var(--border-danger)",
                borderRadius: "8px",
                color: "var(--accent-danger)",
                fontSize: "12px",
              }}
            >
              ❌ {canvaOAuth.error}
            </div>
          )}

          {/* Connected info with token health */}
          {canvaOAuth.connected && (
            <div
              style={{
                marginTop: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  flexWrap: "wrap",
                }}
              >
                {/* Token Health Indicator */}
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontSize: "11px",
                    fontWeight: "500",
                    background:
                      canvaOAuth.tokenHealth === "healthy"
                        ? "var(--bg-success-subtle)"
                        : canvaOAuth.tokenHealth === "expiring_soon"
                          ? "var(--bg-warning-subtle)"
                          : canvaOAuth.tokenHealth === "needs_refresh"
                            ? "var(--bg-warning-subtle)"
                            : canvaOAuth.tokenHealth === "expired"
                              ? "var(--bg-danger-subtle)"
                              : "var(--bg-tertiary)",
                    color:
                      canvaOAuth.tokenHealth === "healthy"
                        ? "var(--accent-success)"
                        : canvaOAuth.tokenHealth === "expiring_soon"
                          ? "var(--accent-warning)"
                          : canvaOAuth.tokenHealth === "needs_refresh"
                            ? "var(--accent-warning)"
                            : canvaOAuth.tokenHealth === "expired"
                              ? "var(--accent-danger)"
                              : "var(--text-secondary)",
                  }}
                >
                  <span style={{ fontSize: "8px" }}>
                    {canvaOAuth.tokenHealth === "healthy"
                      ? "●"
                      : canvaOAuth.tokenHealth === "expired"
                        ? "○"
                        : "◐"}
                  </span>
                  {canvaOAuth.tokenHealth === "healthy"
                    ? "Token Healthy"
                    : canvaOAuth.tokenHealth === "expiring_soon"
                      ? `Expiring in ${canvaOAuth.minutesUntilExpiry}m`
                      : canvaOAuth.tokenHealth === "needs_refresh"
                        ? "Needs Refresh"
                        : canvaOAuth.tokenHealth === "expired"
                          ? "Token Expired"
                          : "Checking..."}
                </span>

                {canvaOAuth.scopes?.length > 0 && (
                  <span style={{ color: "var(--text-secondary)", fontSize: "11px" }}>
                    • {canvaOAuth.scopes.length} scopes granted
                  </span>
                )}

                {/* Auto-refresh indicator */}
                <span style={{ color: "var(--text-secondary)", fontSize: "10px" }}>
                  • Auto-refresh enabled
                </span>
              </div>

              {/* Refresh Button */}
              {(canvaOAuth.tokenHealth === "needs_refresh" ||
                canvaOAuth.tokenHealth === "expiring_soon" ||
                canvaOAuth.tokenHealth === "expired") && (
                <button
                  onClick={handleCanvaRefreshToken}
                  disabled={canvaOAuth.refreshing}
                  style={{
                    padding: "4px 10px",
                    background: canvaOAuth.refreshing ? "var(--text-tertiary)" : "var(--accent-primary)",
                    border: "none",
                    borderRadius: "4px",
                    color: "var(--text-on-accent)",
                    fontSize: "11px",
                    fontWeight: "500",
                    cursor: canvaOAuth.refreshing ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  {canvaOAuth.refreshing ? (
                    <>
                      <span
                        style={{
                          display: "inline-block",
                          width: "10px",
                          height: "10px",
                          border: "2px solid var(--border-primary)",
                          borderTopColor: "var(--text-on-accent)",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                        }}
                      />
                      Refreshing...
                    </>
                  ) : (
                    <>🔄 Refresh Token</>
                  )}
                </button>
              )}
            </div>
          )}

          {/* Canva Services Menu - Shows when connected */}
          {canvaOAuth.connected && (
            <div
              style={{
                marginTop: "20px",
                paddingTop: "20px",
                borderTop: "1px solid var(--border-primary)",
              }}
            >
              <h5
                style={{
                  margin: "0 0 12px 0",
                  color: "var(--text-primary)",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                🎨 Enable Canva Services
              </h5>
              <p
                style={{
                  margin: "0 0 16px 0",
                  color: "var(--text-secondary)",
                  fontSize: "12px",
                }}
              >
                Toggle which Canva services Ollie can act on. Enable a service and its tools switch on.
              </p>

              {/* Services error */}
              {canvaServices.error && (
                <div
                  style={{
                    marginBottom: "12px",
                    padding: "8px 12px",
                    background: "var(--bg-danger-subtle)",
                    border: "1px solid var(--border-danger)",
                    borderRadius: "8px",
                    color: "var(--accent-danger)",
                    fontSize: "12px",
                  }}
                >
                  ❌ {canvaServices.error}
                </div>
              )}

              {/* Services list */}
              {canvaServices.loading ? (
                <div style={{ color: "var(--text-secondary)", fontSize: "12px" }}>
                  Loading services...
                </div>
              ) : (
                <div
                  className="hf-stagger"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                    gap: "12px",
                  }}
                >
                  {canvaServices.services.map((service) => {
                    const isToggling = canvaServices.togglingService === service.id;
                    const serviceIcons = {
                      CANVA: "🎨",
                      CANVA_DESIGN: "✏️",
                      CANVA_FOLDERS: "📁",
                      CANVA_ASSETS: "🖼️",
                      CANVA_BRAND_TEMPLATES: "📑",
                      CANVA_COMMENTS: "💬",
                    };

                    return (
                      <div
                        key={service.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "12px",
                          background: service.enabled ? "var(--bg-success-subtle)" : "var(--bg-tertiary)",
                          border: service.enabled
                            ? "1px solid var(--border-success)"
                            : "1px solid var(--border-primary)",
                          borderRadius: "8px",
                          transition: "all 0.2s ease",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <span style={{ fontSize: "16px" }}>
                            {serviceIcons[service.id] || "⚙️"}
                          </span>
                          <span
                            style={{
                              color: "var(--text-primary)",
                              fontSize: "13px",
                              fontWeight: "500",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "120px",
                            }}
                            title={service.name}
                          >
                            {service.name}
                          </span>
                        </div>

                        {/* Toggle Switch */}
                        <button
                          onClick={() => handleToggleCanvaService(service.id, service.enabled)}
                          disabled={isToggling}
                          style={{
                            position: "relative",
                            width: "44px",
                            height: "24px",
                            borderRadius: "12px",
                            border: "none",
                            background: service.enabled
                              ? "var(--accent-success)"
                              : "var(--text-tertiary)",
                            cursor: isToggling ? "not-allowed" : "pointer",
                            transition: "background 0.2s ease",
                            opacity: isToggling ? 0.6 : 1,
                          }}
                          title={service.enabled ? `Disable ${service.name}` : `Enable ${service.name}`}
                        >
                          <span
                            style={{
                              position: "absolute",
                              top: "2px",
                              left: service.enabled ? "22px" : "2px",
                              width: "20px",
                              height: "20px",
                              borderRadius: "50%",
                              background: "#ffffff",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                              transition: "left 0.2s ease",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "10px",
                              color: "var(--text-secondary)",
                            }}
                          >
                            {isToggling ? "..." : ""}
                          </span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Note about token refresh */}
              <small
                style={{
                  display: "block",
                  marginTop: "16px",
                  color: "var(--text-secondary)",
                  fontSize: "11px",
                }}
              >
                💡 Canva tokens expire every 4 hours. Auto-refresh is enabled to keep your connection active.
              </small>
            </div>
          )}
        </div>
        )}
      </div>

      {/* Custom Tools Section (gated by feature flag) — id is the Custom menu's anchor */}
      {customToolsEnabled && (
      <div
        id="custom-tools"
        style={{
          paddingTop: "24px",
          borderTop: "1px solid var(--border-primary)",
        }}
      >
        <h3>Custom Tools<HelpDot term="custom-mcp" label="What is a Custom MCP?" /></h3>
        <p style={{ color: "var(--text-secondary)", marginBottom: "16px", fontSize: "14px" }}>
          Connect any service — name it, paste your credentials, and we build the tools automatically.
          Works with API keys, tokens, client credentials, and multi-key setups.
        </p>

        {/* Add Custom Tool Form */}
        <form
          onSubmit={handleCustomToolSubmit}
          style={{
            background: "var(--bg-tertiary)",
            borderRadius: "8px",
            padding: "16px",
            marginBottom: "16px",
            border: "1px solid var(--border-primary)",
          }}
        >
          {/* Service Name */}
          <div style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", fontSize: "12px", color: "var(--text-secondary)", marginBottom: "4px" }}>
              Service Name
            </label>
            <input
              type="text"
              placeholder="e.g., Asana, Notion, Basecamp"
              value={customToolForm.platform_name}
              onChange={(e) => setCustomToolForm((prev) => ({ ...prev, platform_name: e.target.value }))}
              style={{
                width: "100%",
                padding: "8px 12px",
                background: "var(--bg-input)",
                border: "1px solid var(--border-secondary)",
                borderRadius: "8px",
                color: "var(--text-primary)",
                fontSize: "14px",
              }}
            />
          </div>

          {/* Dynamic Credential Rows */}
          <div style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", fontSize: "12px", color: "var(--text-secondary)", marginBottom: "4px" }}>
              Credentials
            </label>
            {(customToolForm.credentials || []).map((cred, idx) => (
              <div key={idx} style={{ display: "flex", gap: "8px", marginBottom: "8px", alignItems: "center" }}>
                <input
                  type="text"
                  placeholder="Name (e.g., API_KEY)"
                  value={cred.name}
                  onChange={(e) => {
                    const updated = [...customToolForm.credentials];
                    updated[idx] = { ...updated[idx], name: e.target.value };
                    setCustomToolForm((prev) => ({ ...prev, credentials: updated }));
                  }}
                  style={{
                    width: "140px",
                    padding: "8px 12px",
                    background: "var(--bg-input)",
                    border: "1px solid var(--border-secondary)",
                    borderRadius: "8px",
                    color: "var(--text-primary)",
                    fontSize: "13px",
                    fontFamily: "monospace",
                  }}
                />
                <input
                  type="password"
                  placeholder="Paste value"
                  value={cred.value}
                  onChange={(e) => {
                    const updated = [...customToolForm.credentials];
                    updated[idx] = { ...updated[idx], value: e.target.value };
                    setCustomToolForm((prev) => ({ ...prev, credentials: updated }));
                  }}
                  style={{
                    flex: 1,
                    padding: "8px 12px",
                    background: "var(--bg-input)",
                    border: "1px solid var(--border-secondary)",
                    borderRadius: "8px",
                    color: "var(--text-primary)",
                    fontSize: "14px",
                  }}
                />
                {customToolForm.credentials.length > 1 && (
                  <button
                    type="button"
                    onClick={() => {
                      const updated = customToolForm.credentials.filter((_, i) => i !== idx);
                      setCustomToolForm((prev) => ({ ...prev, credentials: updated }));
                    }}
                    style={{
                      padding: "4px 8px",
                      background: "transparent",
                      border: "1px solid var(--border-primary)",
                      borderRadius: "4px",
                      color: "var(--text-secondary)",
                      cursor: "pointer",
                      fontSize: "12px",
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                setCustomToolForm((prev) => ({
                  ...prev,
                  credentials: [...prev.credentials, { name: "", value: "" }],
                }));
              }}
              style={{
                padding: "4px 12px",
                background: "transparent",
                border: "1px dashed var(--border-primary)",
                borderRadius: "4px",
                color: "var(--text-secondary)",
                cursor: "pointer",
                fontSize: "12px",
              }}
            >
              + Add credential
            </button>
            {customOAuthRefreshEnabled && (
              <button
                type="button"
                onClick={() => {
                  setCustomToolForm((prev) => {
                    const have = new Set((prev.credentials || []).map((c) => (c.name || "").trim().toUpperCase()));
                    const add = [
                      { name: "REFRESH_TOKEN", value: "" },
                      { name: "TOKEN_URL", value: "" },
                      { name: "CLIENT_ID", value: "" },
                      { name: "CLIENT_SECRET", value: "" },
                    ].filter((c) => !have.has(c.name));
                    return { ...prev, credentials: [...(prev.credentials || []), ...add] };
                  });
                }}
                title="Paste an OAuth access token as API_KEY, then fill these in — Optimus refreshes the token for you before it expires."
                style={{
                  marginLeft: "8px",
                  padding: "4px 12px",
                  background: "transparent",
                  border: "1px dashed var(--border-primary)",
                  borderRadius: "4px",
                  color: "var(--text-secondary)",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                + OAuth token (auto-refresh)
              </button>
            )}
            <div style={{ marginTop: "8px" }}>
              <input
                type="text"
                placeholder="Optional — sample IDs for the live test, e.g. spreadsheetId=1abc…, projectId=42"
                value={customToolForm.probe_samples || ""}
                onChange={(e) => setCustomToolForm((prev) => ({ ...prev, probe_samples: e.target.value }))}
                title="Some APIs have no read endpoint that works without an ID (Google Sheets, most CRMs). Give one ID you own and every read tool gets verified against your key before the tool is built."
                style={{
                  width: "100%",
                  padding: "6px 10px",
                  background: "var(--bg-input)",
                  border: "1px solid var(--border-primary)",
                  borderRadius: "4px",
                  color: "var(--text-primary)",
                  fontSize: "12px",
                }}
              />
            </div>
          </div>

          {customToolError && (
            <div style={{ color: "var(--accent-danger)", fontSize: "13px", marginBottom: "8px" }}>
              {customToolError}
            </div>
          )}

          {customToolSuccess && (
            <div
              role="status"
              style={{
                color: "var(--accent-success, #1a7f37)",
                background: "var(--bg-success-subtle, rgba(26,127,55,0.08))",
                border: "1px solid var(--accent-success, #1a7f37)",
                borderRadius: "6px",
                padding: "8px 12px",
                fontSize: "13px",
                marginBottom: "8px",
              }}
            >
              {customToolSuccess}
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <button
              type="submit"
              disabled={customToolSubmitting}
              className="hf-press hf-focus"
              style={{
                padding: "8px 20px",
                background: customToolSubmitting ? "var(--bg-tertiary)" : "var(--accent-primary)",
                color: "var(--text-on-accent)",
                border: "none",
                borderRadius: "8px",
                cursor: customToolSubmitting ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              {customToolSubmitting ? "Building..." : "Build Tools"}
            </button>
            <span style={{ fontSize: "12px", color: "var(--text-secondary)" }}>
              Not sure where to find your API key? Ask Ollie in chat — just say "help me find my [service] API key."
            </span>
          </div>
        </form>

        {/* Custom Tools List */}
        {customToolsLoading && customTools.length === 0 ? (
          <div style={{ textAlign: "center", padding: "20px", color: "var(--text-secondary)", fontSize: "14px" }}>
            Loading custom tools...
          </div>
        ) : customTools.length === 0 ? (
          <div style={{ textAlign: "center", padding: "20px", color: "var(--text-secondary)", fontSize: "14px" }}>
            No custom tools yet. Add your first API above!
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {customTools.map((tool) => (
              <div
                key={tool.id}
                style={{
                  padding: "12px 16px",
                  background: "var(--bg-tertiary)",
                  borderRadius: "8px",
                  border: "1px solid var(--border-primary)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
                    <span style={{ fontWeight: "500", color: "var(--text-primary)", fontSize: "14px" }}>
                      {tool.name}
                    </span>
                    <span
                      style={{
                        padding: "2px 6px",
                        borderRadius: "4px",
                        fontSize: "10px",
                        fontWeight: "600",
                        background: "var(--bg-accent-subtle)",
                        color: "var(--accent-primary)",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Custom
                    </span>
                    <span
                      style={{
                        padding: "2px 8px",
                        borderRadius: "12px",
                        fontSize: "11px",
                        fontWeight: "500",
                        background:
                          tool.status === "ready"
                            ? "var(--bg-success-subtle)"
                            : tool.status === "building" || tool.status === "unverified"
                              ? "var(--bg-warning-subtle)"
                              : "var(--bg-danger-subtle)",
                        color:
                          tool.status === "ready"
                            ? "var(--accent-success)"
                            : tool.status === "building" || tool.status === "unverified"
                              ? "var(--accent-warning)"
                              : "var(--accent-danger)",
                      }}
                    >
                      {tool.status === "ready"
                        ? `Ready${tool.toolCount ? ` (${tool.toolCount} tools)` : ""}`
                        : tool.status === "building"
                          ? "Building..."
                          : tool.status === "unverified"
                            ? "Built, not yet verified"
                            : tool.status === "failed"
                              ? "Failed — delete and try again"
                              : tool.status}
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {tool.status !== "building" && (
                      <button
                        onClick={() => {
                          const isOpen = customToolCredEdit[tool.id];
                          setCustomToolCredEdit((prev) => ({ ...prev, [tool.id]: !isOpen }));
                          if (!isOpen && !customToolCredValues[tool.id]) {
                            // Initialize with one empty row when opening
                            setCustomToolCredValues((prev) => ({
                              ...prev,
                              [tool.id]: [{ name: "API_KEY", value: "" }],
                            }));
                          }
                        }}
                        style={{
                          padding: "4px 10px",
                          background: "transparent",
                          border: "1px solid var(--border-primary)",
                          borderRadius: "4px",
                          color: "var(--text-secondary)",
                          cursor: "pointer",
                          fontSize: "12px",
                        }}
                      >
                        {customToolCredEdit[tool.id] ? "Cancel" : "Update Credentials"}
                      </button>
                    )}
                    <button
                      onClick={() => handleCustomToolDelete(tool.id, tool.name)}
                      style={{
                        padding: "4px 10px",
                        background: "transparent",
                        border: "1px solid var(--border-danger)",
                        borderRadius: "4px",
                        color: "var(--accent-danger)",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                    >
                      {tool.status === "failed" ? "Remove" : "Delete"}
                    </button>
                  </div>
                </div>
                {tool.status === "building" ? (
                  <div style={{ fontSize: "12px", color: "var(--text-secondary)", marginTop: "6px" }}>
                    This can take up to 30 minutes for larger platforms — build time scales with how many endpoints the service exposes, the scopes involved, and testing each tool it builds. You can close this page and check back later.
                  </div>
                ) : tool.description ? (
                  <div style={{ fontSize: "12px", color: "var(--text-secondary)", marginTop: "4px" }}>
                    {tool.description}
                  </div>
                ) : null}
                {customToolCredEdit[tool.id] && (
                  <div style={{ marginTop: "10px" }}>
                    {(customToolCredValues[tool.id] || []).map((cred, idx) => (
                      <div key={idx} style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
                        <input
                          type="text"
                          placeholder="Name"
                          value={cred.name}
                          onChange={(e) => {
                            const updated = [...(customToolCredValues[tool.id] || [])];
                            updated[idx] = { ...updated[idx], name: e.target.value };
                            setCustomToolCredValues((prev) => ({ ...prev, [tool.id]: updated }));
                          }}
                          style={{
                            width: "120px",
                            padding: "6px 8px",
                            borderRadius: "4px",
                            border: "1px solid var(--border-secondary)",
                            background: "var(--bg-input)",
                            color: "var(--text-primary)",
                            fontSize: "12px",
                            fontFamily: "monospace",
                          }}
                        />
                        <input
                          type="password"
                          placeholder="Paste value"
                          value={cred.value}
                          onChange={(e) => {
                            const updated = [...(customToolCredValues[tool.id] || [])];
                            updated[idx] = { ...updated[idx], value: e.target.value };
                            setCustomToolCredValues((prev) => ({ ...prev, [tool.id]: updated }));
                          }}
                          style={{
                            flex: 1,
                            padding: "6px 8px",
                            borderRadius: "4px",
                            border: "1px solid var(--border-secondary)",
                            background: "var(--bg-input)",
                            color: "var(--text-primary)",
                            fontSize: "13px",
                          }}
                        />
                        {(customToolCredValues[tool.id] || []).length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const updated = (customToolCredValues[tool.id] || []).filter((_, i) => i !== idx);
                              setCustomToolCredValues((prev) => ({ ...prev, [tool.id]: updated }));
                            }}
                            style={{ padding: "2px 6px", background: "transparent", border: "1px solid var(--border-primary)", borderRadius: "3px", color: "var(--text-secondary)", cursor: "pointer", fontSize: "11px" }}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "6px" }}>
                      <button
                        type="button"
                        onClick={() => {
                          setCustomToolCredValues((prev) => ({
                            ...prev,
                            [tool.id]: [...(prev[tool.id] || []), { name: "", value: "" }],
                          }));
                        }}
                        style={{ padding: "3px 10px", background: "transparent", border: "1px dashed var(--border-primary)", borderRadius: "4px", color: "var(--text-secondary)", cursor: "pointer", fontSize: "11px" }}
                      >
                        + Add
                      </button>
                      <button
                        onClick={() => handleCustomToolCredUpdate(tool.id)}
                        disabled={customToolCredSaving[tool.id]}
                        style={{
                          padding: "5px 14px",
                          background: "var(--accent-primary)",
                          border: "none",
                          borderRadius: "8px",
                          color: "var(--text-on-accent)",
                          cursor: customToolCredSaving[tool.id] ? "not-allowed" : "pointer",
                          fontSize: "12px",
                          fontWeight: "500",
                          opacity: customToolCredSaving[tool.id] ? 0.7 : 1,
                        }}
                      >
                        {customToolCredSaving[tool.id] ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Tool Integrations Section */}
      <div
        style={{
          paddingTop: "24px",
          borderTop: "1px solid var(--border-primary)",
        }}
      >
        <h3>Built-in connections<HelpDot term="built-in-connections" label="What are built-in connections, and what are their limits?" /></h3>
        <p style={{ color: "var(--text-secondary)", marginBottom: "16px", fontSize: "14px" }}>
          Connections we already built to get you started. Connect a tool and its agents get the matching tools.
          Heads up: a connection only works as far as your key's scopes reach, so some tools may not work with a
          limited key. Want a custom fit? Build a Custom MCP above — it form-fits to your exact key — and ask Ollie if you need help.
        </p>

        {/* Search Bar */}
        <div style={{ marginBottom: "20px" }}>
          <input
            type="text"
            placeholder="🔍 Search integrations by name or description..."
            value={integrationSearch}
            onChange={(e) => setIntegrationSearch(e.target.value)}
            style={{
              width: "100%",
              maxWidth: "400px",
              padding: "10px 15px",
              background: "var(--bg-input)",
              border: "1px solid var(--border-secondary)",
              borderRadius: "8px",
              color: "var(--text-primary)",
              fontSize: "14px",
            }}
          />
        </div>

        {isLoading ? (
          <div
            style={{ textAlign: "center", padding: "40px", color: "var(--text-secondary)" }}
          >
            Loading integrations...
          </div>
        ) : (
          <div
            className="hf-stagger"
            style={{ display: "flex", flexDirection: "column", gap: "16px" }}
          >
            {integrations
              .filter((integration) => {
                if (!integrationSearch.trim()) return true;
                const search = integrationSearch.toLowerCase();
                return (
                  (integration.name || "").toLowerCase().includes(search) ||
                  (integration.description || "")
                    .toLowerCase()
                    .includes(search) ||
                  (integration.id || "").toLowerCase().includes(search) ||
                  (integration.category || "").toLowerCase().includes(search)
                );
              })
              .map((integration) => (
                <IntegrationCard
                  key={integration.id}
                  integration={integration}
                  isConnected={integration.connected}
                  lastTested={integration.lastTested}
                  testStatus={integration.testStatus}
                  onConnect={() => handleConnect(integration)}
                  onTest={() => handleTestExisting(integration)}
                  onDelete={() => handleDelete(integration)}
                  isTesting={isTesting}
                />
              ))}
            {integrations.filter((integration) => {
              if (!integrationSearch.trim()) return true;
              const search = integrationSearch.toLowerCase();
              return (
                (integration.name || "").toLowerCase().includes(search) ||
                (integration.description || "")
                  .toLowerCase()
                  .includes(search) ||
                (integration.id || "").toLowerCase().includes(search) ||
                (integration.category || "").toLowerCase().includes(search)
              );
            }).length === 0 &&
              integrationSearch.trim() && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    color: "var(--text-secondary)",
                  }}
                >
                  No integrations found matching "{integrationSearch}"
                </div>
              )}
          </div>
        )}
      </div>

      {/* Integration Modal */}
      {selectedIntegration && (
        <IntegrationModal
          integration={selectedIntegration}
          credentials={selectedCredentials}
          onSave={handleSave}
          onClose={handleCloseModal}
          onTest={handleTest}
          isSaving={isSaving}
          isTesting={isTesting}
          testResult={testResults}
          isLoadingCredentials={isLoadingCredentials}
        />
      )}

      {/* Incremental Authorization Modal */}
      {incrementalAuthModal.show && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "var(--bg-overlay)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={handleCloseIncrementalAuthModal}
        >
          <div
            style={{
              background: "var(--bg-modal)",
              borderRadius: "16px",
              padding: "24px",
              maxWidth: "420px",
              width: "90%",
              border: "1px solid var(--border-primary)",
              boxShadow: "var(--hf-shadow-3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "16px",
              }}
            >
              <span style={{ fontSize: "32px" }}>🔐</span>
              <div>
                <h3 style={{ margin: 0, color: "var(--text-primary)", fontSize: "18px" }}>
                  Additional Permissions Needed
                </h3>
                <p
                  style={{
                    margin: "4px 0 0 0",
                    color: "var(--text-secondary)",
                    fontSize: "13px",
                  }}
                >
                  {incrementalAuthModal.serviceName} requires extra access
                </p>
              </div>
            </div>

            {/* Explanation */}
            <div
              style={{
                background: "var(--bg-tertiary)",
                borderRadius: "8px",
                padding: "16px",
                marginBottom: "20px",
              }}
            >
              <p
                style={{
                  margin: "0 0 12px 0",
                  color: "var(--text-secondary)",
                  fontSize: "14px",
                  lineHeight: "1.5",
                }}
              >
                To enable <strong>{incrementalAuthModal.serviceName}</strong>,
                Ollie needs permission to access your Google{" "}
                {incrementalAuthModal.serviceName?.toLowerCase()}. You'll be
                redirected to Google to grant this access.
              </p>

              {incrementalAuthModal.missingScopes.length > 0 && (
                <div style={{ marginTop: "12px" }}>
                  <p
                    style={{
                      margin: "0 0 8px 0",
                      color: "var(--text-secondary)",
                      fontSize: "12px",
                      fontWeight: "500",
                    }}
                  >
                    Permissions requested:
                  </p>
                  <ul
                    style={{
                      margin: 0,
                      paddingLeft: "20px",
                      color: "var(--text-secondary)",
                      fontSize: "11px",
                    }}
                  >
                    {incrementalAuthModal.missingScopes
                      .slice(0, 3)
                      .map((scope, i) => (
                        <li key={i} style={{ marginBottom: "4px" }}>
                          {scope.split("/").pop()}
                        </li>
                      ))}
                    {incrementalAuthModal.missingScopes.length > 3 && (
                      <li style={{ color: "var(--text-tertiary)" }}>
                        +{incrementalAuthModal.missingScopes.length - 3} more
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>

            {/* Security Note */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
                marginBottom: "20px",
                padding: "12px",
                background: "var(--bg-success-subtle)",
                borderRadius: "8px",
                border: "1px solid var(--border-success)",
              }}
            >
              <span style={{ fontSize: "14px" }}>🔒</span>
              <p
                style={{
                  margin: 0,
                  color: "var(--accent-success)",
                  fontSize: "12px",
                  lineHeight: "1.4",
                }}
              >
                Your existing Google permissions remain unchanged. You're only
                adding access to{" "}
                {incrementalAuthModal.serviceName?.toLowerCase()}.
              </p>
            </div>

            {/* Bulk Mode Suggestion */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "16px",
                padding: "10px 12px",
                background: "var(--bg-accent-subtle)",
                borderRadius: "8px",
                border: "1px solid var(--border-accent)",
              }}
            >
              <span style={{ fontSize: "14px" }}>⚡</span>
              <p
                style={{
                  margin: 0,
                  color: "var(--accent-primary)",
                  fontSize: "12px",
                  lineHeight: "1.4",
                }}
              >
                <strong>Pro tip:</strong> Need multiple services? Use{" "}
                <button
                  onClick={() => {
                    handleCloseIncrementalAuthModal();
                    setPendingServices([incrementalAuthModal.serviceId]);
                    setBulkAuthMode(true);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    color: "var(--accent-primary)",
                    textDecoration: "underline",
                    cursor: "pointer",
                    padding: 0,
                    fontSize: "12px",
                    fontWeight: "600",
                  }}
                >
                  Bulk Enable Mode
                </button>{" "}
                to authorize them all with one Google sign-in!
              </p>
            </div>

            {/* Buttons */}
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={handleCloseIncrementalAuthModal}
                disabled={incrementalAuthModal.loading}
                style={{
                  padding: "10px 20px",
                  background: "transparent",
                  border: "1px solid var(--border-primary)",
                  borderRadius: "8px",
                  color: "var(--text-secondary)",
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleIncrementalAuth}
                disabled={incrementalAuthModal.loading}
                style={{
                  padding: "10px 20px",
                  background: "var(--accent-success)",
                  border: "none",
                  borderRadius: "8px",
                  color: "var(--text-on-accent)",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: incrementalAuthModal.loading
                    ? "not-allowed"
                    : "pointer",
                  opacity: incrementalAuthModal.loading ? 0.6 : 1,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {incrementalAuthModal.loading ? (
                  <>
                    <span
                      style={{
                        display: "inline-block",
                        width: "14px",
                        height: "14px",
                        border: "2px solid var(--border-primary)",
                        borderTopColor: "var(--text-on-accent)",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                      }}
                    />
                    Redirecting...
                  </>
                ) : (
                  <>
                    <span>🔗</span>
                    Authorize Access
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationsTab;
